import React, { useEffect, useState } from "react";
import { apiUrl } from "../../service/apiUrl";
import {
    Avatar,
    Box,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Typography,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Snackbar,
} from "@mui/material";
import {
    Assignment as AssignmentIcon,
    CalendarToday as CalendarTodayIcon,
    Close as CloseIcon,
    ExitToApp as ExitToAppIcon,
    History as HistoryIcon,
    Home as HomeIcon,
    WorkHistory as WorkHistoryIcon,
    MoreTime as OT,
    ContentPaste as ContentPasteIcon,
    Addchart as OverTime,
    CloudUpload as CloudUploadIcon,
    EventNote as EventNoteIcon,
    Schedule as ScheduleIcon,
    CalendarMonth as CalendarMonthIcon,
    PersonAdd as PersonAddIcon, // icon for employee register
    LocationOn as LocationOnIcon,
    Badge as BadgeIcon,
    AccountTree as AccountTreeIcon,
    SpaceDashboard as SpaceDashboardIcon,
    Tune as TuneIcon,
    SwapVert as SwapVertIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchJobItems } from "../../actions/jobAction";
import { fetchJobs } from "../../actions/AssignAction";
import { uploadProfileImageAction, fetchUserDetails } from "../../actions/authActions";
import Logo from "../../assets/logo6.png";
import axios from "axios";

// Service numbers whose profile image is confirmed missing (after 3 failed
// attempts). Declared outside the component so it persists across sidebar
// open/close remounts within the session — once we know an image doesn't
// exist, we never call GetUserImg for that service number again.
const failedProfileImageServiceNos = new Set();

const Sidebar = ({ open, toggleDrawer }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    // Get all state from Redux
    const {
        serviceNo,
        userImage,
        employeeName,
        department,
        role,
        uploadingImage,
        uploadImageError,
        userData
    } = useSelector((state) => state.auth || {});
    
    // Get jobs data from Redux store
    const { jobValue } = useSelector((state) => state.assignJob || {});
    
    const [loadingImage, setLoadingImage] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [imageAttempt, setImageAttempt] = useState(0);
    // Cache-busting timestamp for the profile image, only regenerated when
    // we actually retry — not on every render — so it doesn't refetch
    // unnecessarily.
    const [imageCacheBust, setImageCacheBust] = useState(() => Date.now());
    const [userDisplayName, setUserDisplayName] = useState("");
    const [userDisplayServiceNo, setUserDisplayServiceNo] = useState("");
    const [userDisplayRole, setUserDisplayRole] = useState("");
    const [userDisplayDepartment, setUserDisplayDepartment] = useState("");
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [uploadError, setUploadError] = useState("");
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const fileInputRef = React.useRef(null);
    const [previewImage, setPreviewImage] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState(() => ({
        leave: window.location.pathname.startsWith("/leave"),
    }));
    const toggleGroup = (group) =>
        setExpandedGroups((prev) => ({ ...prev, [group]: !prev[group] }));
    
    // Default image URL
    const DEFAULT_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Ccircle cx='12' cy='8' r='4' fill='%23999'/%3E%3Cpath d='M12 14c-6 0-8 3-8 3v7h16v-7s-2-3-8-3z' fill='%23999'/%3E%3C/svg%3E";

    // Update user image when service number changes
    useEffect(() => {
        if (serviceNo) {
            setImageAttempt(0);
            setImageCacheBust(Date.now());
            if (failedProfileImageServiceNos.has(serviceNo)) {
                // Already confirmed missing in this session — don't call
                // GetUserImg again, just show the default avatar.
                setLoadingImage(false);
                setImageError(true);
            } else {
                setLoadingImage(true);
                setImageError(false);
            }

            // Fetch jobs
            const date = new Date().toISOString().split("T")[0];
            dispatch(fetchJobs(date, serviceNo));
        }
    }, [serviceNo, dispatch]);

    // Fetch user details if service number is available
    useEffect(() => {
        if (serviceNo) {
            fetchUserDetailsFromAPI();
        } else {
            // If no service number, use Redux state
            setUserDisplayName(employeeName || "");
            setUserDisplayServiceNo(serviceNo || "");
            setUserDisplayDepartment(department || "");
            setUserDisplayRole(role || "");
        }
    }, [serviceNo, employeeName, department, role]);

    // Show error alert when uploadImageError changes
    useEffect(() => {
        if (uploadImageError) {
            setUploadError(uploadImageError);
            setShowErrorAlert(true);
        }
    }, [uploadImageError]);

    // Auto-hide alerts after 3 seconds
    useEffect(() => {
        if (showSuccessAlert) {
            const timer = setTimeout(() => {
                setShowSuccessAlert(false);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [showSuccessAlert]);

    useEffect(() => {
        if (showErrorAlert) {
            const timer = setTimeout(() => {
                setShowErrorAlert(false);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [showErrorAlert]);

    const fetchUserDetailsFromAPI = async () => {
        if (!serviceNo) return;
        
        setDetailsLoading(true);
        try {
            console.log("Fetching user details for serviceNo:", serviceNo);
            const res = await axios.get(
                `/UserDetails/GetUserDetailsByUser?sno=${serviceNo}`
            );
            
            console.log("User details API response:", res.data);
            
            const resultSet = res.data?.resultSet;
            if (resultSet && Array.isArray(resultSet) && resultSet.length > 0) {
                const userData = resultSet[0];
                console.log("User data from API:", userData);
                
                const name = userData?.Name || "";
                const dept = userData?.Department || "";
                // Try different possible field names for role
                const userRole = userData?.Designation || 
                               userData?.Role || 
                               userData?.role || 
                               userData?.designation || 
                               "";
                
                console.log("Extracted role from API:", userRole);
                
                // Set the role from API
                setUserDisplayRole(userRole);
                setUserDisplayDepartment(dept);
                setUserDisplayName(name);
                setUserDisplayServiceNo(serviceNo);
                
                // Update Redux store if needed
                dispatch(fetchUserDetails(serviceNo));
            } else {
                console.log("No data in resultSet, using Redux state");
                setUserDisplayRole(role || "");
                setUserDisplayDepartment(department || "");
                setUserDisplayName(employeeName || "");
                setUserDisplayServiceNo(serviceNo);
            }
        } catch (err) {
            console.error("Failed to fetch user details:", err);
            // Fallback to Redux state on error
            setUserDisplayRole(role || "");
            setUserDisplayDepartment(department || "");
            setUserDisplayName(employeeName || "");
            setUserDisplayServiceNo(serviceNo);
        } finally {
            setDetailsLoading(false);
        }
    };

    // Handle image loading error — retries up to 3 times, then gives up
    // permanently for this service number so we stop hitting the API.
    const handleImageError = () => {
        setLoadingImage(false);
        setImageAttempt((prev) => {
            const nextAttempt = prev + 1;
            const maxAttempts = 3;
            if (nextAttempt < maxAttempts && serviceNo) {
                setLoadingImage(true);
                setTimeout(() => {
                    setImageCacheBust(Date.now());
                }, 600 * nextAttempt);
            } else {
                if (serviceNo) failedProfileImageServiceNos.add(serviceNo);
                setImageError(true);
            }
            return nextAttempt;
        });
    };

    // Handle image load success
    const handleImageLoad = () => {
        setLoadingImage(false);
        setImageError(false);
        setImageAttempt(0);
        if (serviceNo) failedProfileImageServiceNos.delete(serviceNo);
    };

    // Handle profile image selection
    const handleProfileImageChange = (event) => {
        const file = event.target.files[0];

        if (file) {
            // Validate file type - support PNG, JPEG, etc.
            const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
            if (!validTypes.includes(file.type)) {
                setUploadError("Please select a valid image file (JPEG, JPG, PNG, GIF, or WebP)");
                setShowErrorAlert(true);
                return;
            }

            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                setUploadError("Image size should not exceed 5MB");
                setShowErrorAlert(true);
                return;
            }

            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewImage(reader.result);
                setSelectedFile(file);
                setOpenPreviewDialog(true);
            };
            reader.readAsDataURL(file);
        }

        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    // Handle closing preview without upload
    const handleClosePreview = () => {
        setOpenPreviewDialog(false);
        setPreviewImage(null);
        setSelectedFile(null);
    };

    // Handle saving/uploading the image
    const handleSaveImage = async () => {
        if (!selectedFile || !serviceNo) return;
        
        try {
            const result = await dispatch(uploadProfileImageAction(serviceNo, selectedFile));
            
            if (result.success) {
                setUploadSuccess(true);
                setShowSuccessAlert(true);
                handleClosePreview();
            } else {
                setUploadError(result.error || "Failed to upload image");
                setShowErrorAlert(true);
            }
        } catch (error) {
            console.error("Image upload error:", error);
            setUploadError("Failed to upload profile picture. Please try again.");
            setShowErrorAlert(true);
        }
    };

    // Handle closing alerts
    const handleCloseErrorAlert = () => {
        setShowErrorAlert(false);
    };

    const handleCloseSuccessAlert = () => {
        setShowSuccessAlert(false);
    };

    // Define menu items
    const allowedOTUsers = ["3000158","3000159","3000694","1004507","3001230","3000821","0001809"];
    const isOTAllowed = allowedOTUsers.includes(String(serviceNo));
    
    // Check if user has role "A" for admin privileges
    // Use the role from API first, fallback to Redux state
    const currentRole = userDisplayRole || role;
    console.log("Current role for menu:", currentRole);
    const isRoleA = currentRole === "A";
    console.log("isRoleA check:", isRoleA);

    // Base menu items for all users
    const baseMenuItems = [
        { text: "Home", path: "/home", icon: <HomeIcon /> },
        { text: "User Profile", path: "/userProfile", icon: <CalendarTodayIcon /> },
    ];

    // Admin-only menu items (only for role "A")
    const adminMenuItems = isRoleA ? [
        { text: "Shift Exchange", path: "/shiftExchange", icon: <WorkHistoryIcon /> },
        { text: "Roster Management", path: "/rosterManagement", icon: <CalendarMonthIcon /> },
        { text: "Employee Register", path: "/employeeRegister", icon: <PersonAddIcon /> },
        // Sub-links shown indented under "Employee Register"
        { text: "Manage Lookups", path: "/employeeRegister/lookups", icon: <LocationOnIcon />, sub: true },
        // Leave module
        // { text: "Leave Management", group: "leave", icon: <CalendarTodayIcon /> },
        // { text: "Leave Dashboard", path: "/leave/dashboard", icon: <SpaceDashboardIcon />, sub: true, parent: "leave" },
        // { text: "Leave Application", path: "/leave/application", icon: <AssignmentIcon />, sub: true, parent: "leave" },
        // { text: "Leave Adjustments", path: "/leave/adjustments", icon: <SwapVertIcon />, sub: true, parent: "leave" },
        // { text: "Leave History", path: "/leave/history", icon: <HistoryIcon />, sub: true, parent: "leave" },
        // // { text: "Holiday Calendar", path: "/leave/holidays", icon: <CalendarMonthIcon />, sub: true, parent: "leave" },
        // { text: "Leave Configuration", path: "/leave/configuration", icon: <TuneIcon />, sub: true, parent: "leave" },
    ] : [];

    // Menu items for all users
    const commonMenuItems = [
        // { text: "Roster Schedule", path: "/rosterSchedule", icon: <ScheduleIcon /> },
        ...(isOTAllowed
            ? [{ text: "Management Info", path: "/overTimeView", icon: <OverTime /> }]
            : []),
        ...(jobValue && jobValue.length > 0 ? [
            { text: "Assign OT", path: "/assignOT", icon: <OT /> },
            { text: "Holiday Assign OT", path: "/holidayOT", icon: <OT /> },
            { text: "OT Details", path: "/otDetails", icon: <ContentPasteIcon /> },
        ] : []),
        { text: "Log Out", path: "/logout", icon: <ExitToAppIcon /> },
    ];

    // Combine all menu items based on user role
    const menuItems = [
        ...baseMenuItems,
        ...adminMenuItems,
        ...commonMenuItems,
    ];

    console.log("Menu items to display:", menuItems);

    const handleNavigation = (path) => {
        if (path === "/jobHistory" || path === "/jobHistoryAll") {
            dispatch(fetchJobItems(new Date().toISOString().split("T")[0], serviceNo, currentRole));
        }
        if (path === "/logout") {
            localStorage.clear();
            navigate("/");
        } else if (path === "/rosterSchedule" || path === "/rosterManagement") {
            navigate(path);
        } else {
            navigate(path, {
                state: { 
                    employeeId: serviceNo, 
                    department: userDisplayDepartment || department, 
                    role: currentRole 
                },
            });
        }
        toggleDrawer(false);
    };

    // Get image source. imageCacheBust only changes when we deliberately
    // retry (see handleImageError), not on every render, so this doesn't
    // regenerate — and therefore doesn't re-request — on unrelated re-renders.
    const imageSource =
        serviceNo && !failedProfileImageServiceNos.has(serviceNo)
            ? (userImage || apiUrl(`User/GetUserImg?Sno=${serviceNo}&t=${imageCacheBust}`))
            : DEFAULT_IMAGE;

    return (
        <>
            <Drawer
                open={open}
                onClose={() => toggleDrawer(false)}
                anchor="left"
                sx={{
                    "& .MuiDrawer-paper": {
                        width: 280,
                        bgcolor: "transparent",
                        borderRight: "none",
                        transition: "transform 0.3s ease",
                        transform: open ? "translateX(0)" : "translateX(-100%)",
                        boxShadow: "0 0 15px rgba(0, 0, 0, 0.5)",
                        borderTopRightRadius: 40,
                        borderBottomRightRadius: 40,
                        backdropFilter: "blur(10px)",
                        scrollbarWidth: "none",
                        msOverflowStyle: "none",
                        "&::-webkit-scrollbar": { display: "none" },
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        px: 2,
                        py: 3,
                        color: "white",
                    }}
                >
                    {/* Header Section */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                        <img
                            src={Logo}
                            alt="Logo"
                            style={{
                                height: 70,
                                objectFit: "contain",
                                borderRadius: "5px",
                            }}
                        />
                        <IconButton onClick={() => toggleDrawer(false)} sx={{ color: "white" }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    
                    {/* Profile Section */}
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            mt: 2,
                            textAlign: "center",
                            width: "100%",
                        }}
                    >
                        <Box sx={{ position: "relative", mb: 2 }}>
                            {loadingImage && (
                                <CircularProgress
                                    sx={{
                                        position: "absolute",
                                        top: "50%",
                                        left: "50%",
                                        marginTop: "-65px",
                                        marginLeft: "-65px",
                                        color: "#ddd"
                                    }}
                                    size={130}
                                />
                            )}
                            <Box
                                onClick={() => fileInputRef.current?.click()}
                                sx={{
                                    position: "relative",
                                    cursor: uploadingImage ? "not-allowed" : "pointer",
                                    opacity: uploadingImage ? 0.6 : 1,
                                    transition: "all 0.3s ease",
                                    "&:hover": {
                                        filter: uploadingImage ? "none" : "brightness(0.9)",
                                        transform: uploadingImage ? "none" : "scale(1.05)",
                                    },
                                }}
                            >
                                <Avatar
                                    src={imageError ? DEFAULT_IMAGE : imageSource}
                                    onError={handleImageError}
                                    onLoad={handleImageLoad}
                                    sx={{
                                        width: 130,
                                        height: 130,
                                        border: "3px solid #ddd",
                                        backgroundColor: "#f0f0f0",
                                    }}
                                />
                                {!uploadingImage && (
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            bottom: 5,
                                            right: 5,
                                            backgroundColor: "#1e0153",
                                            borderRadius: "50%",
                                            padding: "8px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: "white",
                                            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                                        }}
                                    >
                                        <CloudUploadIcon sx={{ fontSize: 20 }} />
                                    </Box>
                                )}
                            </Box>

                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleProfileImageChange}
                                style={{ display: "none" }}
                                disabled={uploadingImage}
                            />
                        </Box>

                        {/* Success/Error Messages inside Sidebar */}
                        {showSuccessAlert && (
                            <Alert 
                                severity="success" 
                                sx={{ 
                                    mb: 2, 
                                    width: "100%", 
                                    fontSize: "0.75rem",
                                    '& .MuiAlert-message': {
                                        width: '100%'
                                    }
                                }}
                                onClose={handleCloseSuccessAlert}
                            >
                                Profile picture updated successfully!
                            </Alert>
                        )}
                        
                        {showErrorAlert && (
                            <Alert 
                                severity="error" 
                                sx={{ 
                                    mb: 2, 
                                    width: "100%", 
                                    fontSize: "0.75rem",
                                    '& .MuiAlert-message': {
                                        width: '100%'
                                    }
                                }}
                                onClose={handleCloseErrorAlert}
                            >
                                {uploadError}
                            </Alert>
                        )}

                        {detailsLoading ? (
                            <CircularProgress size={24} sx={{ color: "#fff", my: 2 }} />
                        ) : (
                            <>
                                <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700, textTransform: "capitalize" }}>
                                    {userDisplayName || employeeName || "User"}
                                </Typography>
                                <Typography variant="subtitle2" sx={{ color: "rgba(255,255,255,0.85)", mt: 0.5 }}>
                                    {serviceNo ? `Service No: ${serviceNo}` : "Service No: —"}
                                </Typography>
                                {(userDisplayDepartment || userDisplayRole) && (
                                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", mt: 0.5 }}>
                                        {userDisplayDepartment || department || ""}
                                        {(userDisplayDepartment || department) && (userDisplayRole || role) ? " • " : ""}
                                        {userDisplayRole || role || ""}
                                    </Typography>
                                )}
                            </>
                        )}
                    </Box>

                    {/* Menu Items */}
                    <List sx={{ mt: 4 }}>
                        {menuItems
                            .filter((item) => !item.parent || expandedGroups[item.parent])
                            .map((item) => (
                            <ListItem
                                button
                                key={item.text}
                                onClick={() =>
                                    item.group
                                        ? toggleGroup(item.group)
                                        : handleNavigation(item.path)
                                }                                sx={{
                                    my: item.sub ? 0.5 : 1,
                                    ml: item.sub ? 3 : 0,
                                    width: item.sub ? "calc(100% - 24px)" : "100%",
                                    backgroundColor: item.path === window.location.pathname ? "#E0A300" : "transparent",
                                    color: item.path === window.location.pathname ? "white" : "white",
                                    borderRadius: 2,
                                    fontWeight: item.sub ? "normal" : "bold",
                                    fontSize: item.sub ? 15 : 18,
                                    transition: "background-color 0.12s ease",
                                    "&:hover": {
                                        backgroundColor: "#F2B807",
                                    },
                                }}
                            >
                                <ListItemAvatar sx={item.sub ? { minWidth: 40 } : undefined}>
                                    <Avatar
                                        sx={{
                                            color: item.path === window.location.pathname ? "white" : "white",
                                            backgroundColor: "transparent",
                                            width: item.sub ? 28 : 40,
                                            height: item.sub ? 28 : 40,
                                        }}
                                    >
                                        {item.icon}
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={item.text}
                                    primaryTypographyProps={item.sub ? { fontSize: 14 } : undefined}
                                />
                                {item.group &&
                                    (expandedGroups[item.group] ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
                            </ListItem>
                        ))}
                    </List>
                </Box>
            </Drawer>

            {/* Image Preview Dialog */}
            <Dialog
                open={openPreviewDialog}
                onClose={handleClosePreview}
                maxWidth="sm"
                fullWidth
                sx={{
                    "& .MuiDialog-paper": {
                        borderRadius: 2,
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 700, color: "#1e0153" }}>
                    Preview Profile Picture
                </DialogTitle>
                <DialogContent sx={{ py: 3, textAlign: "center" }}>
                    {previewImage && (
                        <Box>
                            <Avatar
                                src={previewImage}
                                sx={{
                                    width: 250,
                                    height: 250,
                                    mx: "auto",
                                    mb: 2,
                                    border: "3px solid #1e0153",
                                }}
                            />
                            <Typography variant="body2" sx={{ color: "#666", mt: 2 }}>
                                Does this look good? Click "Save" to update your profile picture.
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button
                        onClick={handleClosePreview}
                        variant="outlined"
                        sx={{
                            color: "#999",
                            borderColor: "#ccc",
                            "&:hover": { borderColor: "#999" }
                        }}
                        disabled={uploadingImage}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSaveImage}
                        variant="contained"
                        sx={{
                            bgcolor: "#1e0153",
                            "&:hover": { bgcolor: "#333" }
                        }}
                        disabled={uploadingImage}
                    >
                        {uploadingImage ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <CircularProgress size={20} sx={{ color: "white" }} />
                                Saving...
                            </Box>
                        ) : (
                            "Save"
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Global Snackbar Notifications */}
            <Snackbar 
                open={showSuccessAlert} 
                autoHideDuration={3000} 
                onClose={handleCloseSuccessAlert}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSuccessAlert} severity="success" sx={{ width: '100%' }}>
                    Profile picture updated successfully!
                </Alert>
            </Snackbar>

            <Snackbar 
                open={showErrorAlert} 
                autoHideDuration={3000} 
                onClose={handleCloseErrorAlert}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseErrorAlert} severity="error" sx={{ width: '100%' }}>
                    {uploadError}
                </Alert>
            </Snackbar>
        </>
    );
};

export default Sidebar;





// import React, { useEffect, useState } from "react";
// import {
//     Avatar,
//     Box,
//     Drawer,
//     IconButton,
//     List,
//     ListItem,
//     ListItemAvatar,
//     ListItemText,
//     Typography,
//     CircularProgress,
//     Alert,
//     Dialog,
//     DialogTitle,
//     DialogContent,
//     DialogActions,
//     Button,
//     Snackbar,
// } from "@mui/material";
// import {
//     Assignment as AssignmentIcon,
//     CalendarToday as CalendarTodayIcon,
//     Close as CloseIcon,
//     ExitToApp as ExitToAppIcon,
//     History as HistoryIcon,
//     Home as HomeIcon,
//     WorkHistory as WorkHistoryIcon,
//     MoreTime as OT,
//     ContentPaste as ContentPasteIcon,
//     Addchart as OverTime,
//     CloudUpload as CloudUploadIcon,
//     EventNote as EventNoteIcon,
//     Schedule as ScheduleIcon,
//     CalendarMonth as CalendarMonthIcon,
// } from "@mui/icons-material";
// import { useDispatch, useSelector } from "react-redux";
// import { useNavigate } from "react-router-dom";
// import { fetchJobItems } from "../../actions/jobAction";
// import { fetchJobs } from "../../actions/AssignAction";
// import { uploadProfileImageAction, fetchUserDetails } from "../../actions/authActions";
// import Logo from "../../assets/logo6.png";
// import axios from "axios";
// import MuiAlert from '@mui/material/Alert';

// const Sidebar = ({ open, toggleDrawer }) => {
//     const dispatch = useDispatch();
//     const navigate = useNavigate();
    
//     // Get all state from Redux
//     const {
//         serviceNo,
//         userImage,
//         employeeName,
//         department,
//         role,
//         uploadingImage,
//         uploadImageError,
//         userData
//     } = useSelector((state) => state.auth || {});
    
//     // Get jobs data from Redux store
//     const { jobValue } = useSelector((state) => state.assignJob || {});
    
//     const [loadingImage, setLoadingImage] = useState(false);
//     const [imageError, setImageError] = useState(false);
//     const [userDisplayName, setUserDisplayName] = useState("");
//     const [userDisplayServiceNo, setUserDisplayServiceNo] = useState("");
//     const [detailsLoading, setDetailsLoading] = useState(false);
//     const [uploadSuccess, setUploadSuccess] = useState(false);
//     const [uploadError, setUploadError] = useState("");
//     const [showErrorAlert, setShowErrorAlert] = useState(false);
//     const [showSuccessAlert, setShowSuccessAlert] = useState(false);
//     const fileInputRef = React.useRef(null);
//     const [previewImage, setPreviewImage] = useState(null);
//     const [selectedFile, setSelectedFile] = useState(null);
//     const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
    
//     // Default image URL
//     const DEFAULT_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Ccircle cx='12' cy='8' r='4' fill='%23999'/%3E%3Cpath d='M12 14c-6 0-8 3-8 3v7h16v-7s-2-3-8-3z' fill='%23999'/%3E%3C/svg%3E";

//     // Update user image when service number changes
//     useEffect(() => {
//         if (serviceNo) {
//             setLoadingImage(true);
//             setImageError(false);
            
//             // Fetch jobs
//             const date = new Date().toISOString().split("T")[0];
//             dispatch(fetchJobs(date, serviceNo));
//         }
//     }, [serviceNo, dispatch]);

//     // Fetch user details if service number is available
//     useEffect(() => {
//         if (serviceNo && (!employeeName || !department || !role)) {
//             fetchUserDetailsFromAPI();
//         }
        
//         if (serviceNo && employeeName) {
//             setUserDisplayName(employeeName);
//             setUserDisplayServiceNo(serviceNo);
//         }
//     }, [serviceNo, employeeName, department, role]);

//     // Show error alert when uploadImageError changes
//     useEffect(() => {
//         if (uploadImageError) {
//             setUploadError(uploadImageError);
//             setShowErrorAlert(true);
//         }
//     }, [uploadImageError]);

//     // Auto-hide alerts after 3 seconds
//     useEffect(() => {
//         if (showSuccessAlert) {
//             const timer = setTimeout(() => {
//                 setShowSuccessAlert(false);
//             }, 3000);
//             return () => clearTimeout(timer);
//         }
//     }, [showSuccessAlert]);

//     useEffect(() => {
//         if (showErrorAlert) {
//             const timer = setTimeout(() => {
//                 setShowErrorAlert(false);
//             }, 3000);
//             return () => clearTimeout(timer);
//         }
//     }, [showErrorAlert]);

//     const fetchUserDetailsFromAPI = async () => {
//         if (!serviceNo) return;
        
//         setDetailsLoading(true);
//         try {
//             const res = await axios.get(
//                 `https://daladamaligawa.dockyardsoftware.com/UserDetails/GetUserDetailsByUser?sno=${serviceNo}`
//             );
            
//             console.log("User details response:", res.data);
            
//             const resultSet = res.data?.resultSet;
//             if (resultSet && Array.isArray(resultSet) && resultSet.length > 0) {
//                 const userData = resultSet[0];
//                 const name = userData?.Name || "";
//                 const dept = userData?.Department || "";
//                 const role = userData?.Designation || "";
                
//                 setUserDisplayName(name);
//                 setUserDisplayServiceNo(serviceNo);
                
//                 // Update Redux store
//                 dispatch(fetchUserDetails(serviceNo));
//             } else {
//                 setUserDisplayName(employeeName || "");
//                 setUserDisplayServiceNo(serviceNo);
//             }
//         } catch (err) {
//             console.error("Failed to fetch user details:", err);
//             setUserDisplayName(employeeName || "");
//             setUserDisplayServiceNo(serviceNo);
//         } finally {
//             setDetailsLoading(false);
//         }
//     };

//     // Handle image loading error
//     const handleImageError = () => {
//         setImageError(true);
//         setLoadingImage(false);
//     };

//     // Handle image load success
//     const handleImageLoad = () => {
//         setLoadingImage(false);
//         setImageError(false);
//     };

//     // Handle profile image selection
//     const handleProfileImageChange = (event) => {
//         const file = event.target.files[0];

//         if (file) {
//             // Validate file type - support PNG, JPEG, etc.
//             const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
//             if (!validTypes.includes(file.type)) {
//                 setUploadError("Please select a valid image file (JPEG, JPG, PNG, GIF, or WebP)");
//                 setShowErrorAlert(true);
//                 return;
//             }

//             // Validate file size (max 5MB)
//             if (file.size > 5 * 1024 * 1024) {
//                 setUploadError("Image size should not exceed 5MB");
//                 setShowErrorAlert(true);
//                 return;
//             }

//             // Create preview
//             const reader = new FileReader();
//             reader.onloadend = () => {
//                 setPreviewImage(reader.result);
//                 setSelectedFile(file);
//                 setOpenPreviewDialog(true);
//             };
//             reader.readAsDataURL(file);
//         }

//         // Reset file input
//         if (fileInputRef.current) {
//             fileInputRef.current.value = "";
//         }
//     };

//     // Handle closing preview without upload
//     const handleClosePreview = () => {
//         setOpenPreviewDialog(false);
//         setPreviewImage(null);
//         setSelectedFile(null);
//     };

//     // Handle saving/uploading the image
//     const handleSaveImage = async () => {
//         if (!selectedFile || !serviceNo) return;
        
//         try {
//             const result = await dispatch(uploadProfileImageAction(serviceNo, selectedFile));
            
//             if (result.success) {
//                 setUploadSuccess(true);
//                 setShowSuccessAlert(true);
//                 handleClosePreview();
//             } else {
//                 setUploadError(result.error || "Failed to upload image");
//                 setShowErrorAlert(true);
//             }
//         } catch (error) {
//             console.error("Image upload error:", error);
//             setUploadError("Failed to upload profile picture. Please try again.");
//             setShowErrorAlert(true);
//         }
//     };

//     // Handle closing alerts
//     const handleCloseErrorAlert = () => {
//         setShowErrorAlert(false);
//     };

//     const handleCloseSuccessAlert = () => {
//         setShowSuccessAlert(false);
//     };

//     // Define menu items
//     const allowedOTUsers = ["3000158","3000159","3000694","1004507","3001230","3000821","0001809"];
//     const isOTAllowed = allowedOTUsers.includes(String(serviceNo));

//     const baseMenuItems = [
//         { text: "Home", path: "/home", icon: <HomeIcon /> },
//         { text: "User Profile", path: "/userProfile", icon: <CalendarTodayIcon /> },
//     ];

//     const menuItems = [
//         ...baseMenuItems,
//         { text: "Shift Exchange", path: "/shiftExchange", icon: <WorkHistoryIcon /> },
//         { text: "Roster Schedule", path: "/rosterSchedule", icon: <ScheduleIcon /> },
//         { text: "Roster Management", path: "/rosterManagement", icon: <CalendarMonthIcon /> },
//         ...(isOTAllowed
//             ? [{ text: "Management Info", path: "/overTimeView", icon: <OverTime /> }]
//             : []),
//         ...(jobValue && jobValue.length > 0 ? [
//             { text: "Assign OT", path: "/assignOT", icon: <OT /> },
//             { text: "Holiday Assign OT", path: "/holidayOT", icon: <OT /> },
//             { text: "OT Details", path: "/otDetails", icon: <ContentPasteIcon /> },
//         ] : []),
//         { text: "Log Out", path: "/logout", icon: <ExitToAppIcon /> },
//     ];

//     const handleNavigation = (path) => {
//         if (path === "/jobHistory" || path === "/jobHistoryAll") {
//             dispatch(fetchJobItems(new Date().toISOString().split("T")[0], serviceNo, role));
//         }
//         if (path === "/logout") {
//             localStorage.clear();
//             navigate("/");
//         } else if (path === "/rosterSchedule" || path === "/rosterManagement") {
//             navigate(path);
//         } else {
//             navigate(path, {
//                 state: { employeeId: serviceNo, department, role },
//             });
//         }
//         toggleDrawer(false);
//     };

//     // Get image source
//     const imageSource = userImage || 
//         (serviceNo ? `https://daladamaligawa.dockyardsoftware.com/User/GetUserImg?Sno=${serviceNo}` : DEFAULT_IMAGE);

//     return (
//         <>
//             <Drawer
//                 open={open}
//                 onClose={() => toggleDrawer(false)}
//                 anchor="left"
//                 sx={{
//                     "& .MuiDrawer-paper": {
//                         width: 280,
//                         bgcolor: "transparent",
//                         borderRight: "none",
//                         transition: "transform 0.3s ease",
//                         transform: open ? "translateX(0)" : "translateX(-100%)",
//                         boxShadow: "0 0 15px rgba(0, 0, 0, 0.5)",
//                         borderTopRightRadius: 40,
//                         borderBottomRightRadius: 40,
//                         backdropFilter: "blur(10px)",
//                     },
//                 }}
//             >
//                 <Box
//                     sx={{
//                         display: "flex",
//                         flexDirection: "column",
//                         height: "100%",
//                         px: 2,
//                         py: 3,
//                         color: "white",
//                     }}
//                 >
//                     {/* Header Section */}
//                     <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
//                         <img
//                             src={Logo}
//                             alt="Logo"
//                             style={{
//                                 height: 70,
//                                 objectFit: "contain",
//                                 borderRadius: "5px",
//                             }}
//                         />
//                         <IconButton onClick={() => toggleDrawer(false)} sx={{ color: "white" }}>
//                             <CloseIcon />
//                         </IconButton>
//                     </Box>
                    
//                     {/* Profile Section */}
//                     <Box
//                         sx={{
//                             display: "flex",
//                             flexDirection: "column",
//                             alignItems: "center",
//                             justifyContent: "center",
//                             mt: 2,
//                             textAlign: "center",
//                             width: "100%",
//                         }}
//                     >
//                         <Box sx={{ position: "relative", mb: 2 }}>
//                             {loadingImage && (
//                                 <CircularProgress
//                                     sx={{
//                                         position: "absolute",
//                                         top: "50%",
//                                         left: "50%",
//                                         marginTop: "-65px",
//                                         marginLeft: "-65px",
//                                         color: "#ddd"
//                                     }}
//                                     size={130}
//                                 />
//                             )}
//                             <Box
//                                 onClick={() => fileInputRef.current?.click()}
//                                 sx={{
//                                     position: "relative",
//                                     cursor: uploadingImage ? "not-allowed" : "pointer",
//                                     opacity: uploadingImage ? 0.6 : 1,
//                                     transition: "all 0.3s ease",
//                                     "&:hover": {
//                                         filter: uploadingImage ? "none" : "brightness(0.9)",
//                                         transform: uploadingImage ? "none" : "scale(1.05)",
//                                     },
//                                 }}
//                             >
//                                 <Avatar
//                                     src={imageError ? DEFAULT_IMAGE : imageSource}
//                                     onError={handleImageError}
//                                     onLoad={handleImageLoad}
//                                     sx={{
//                                         width: 130,
//                                         height: 130,
//                                         border: "3px solid #ddd",
//                                         backgroundColor: "#f0f0f0",
//                                     }}
//                                 />
//                                 {!uploadingImage && (
//                                     <Box
//                                         sx={{
//                                             position: "absolute",
//                                             bottom: 5,
//                                             right: 5,
//                                             backgroundColor: "#1e0153",
//                                             borderRadius: "50%",
//                                             padding: "8px",
//                                             display: "flex",
//                                             alignItems: "center",
//                                             justifyContent: "center",
//                                             color: "white",
//                                             boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
//                                         }}
//                                     >
//                                         <CloudUploadIcon sx={{ fontSize: 20 }} />
//                                     </Box>
//                                 )}
//                             </Box>

//                             <input
//                                 ref={fileInputRef}
//                                 type="file"
//                                 accept="image/*"
//                                 onChange={handleProfileImageChange}
//                                 style={{ display: "none" }}
//                                 disabled={uploadingImage}
//                             />
//                         </Box>

//                         {/* Success/Error Messages inside Sidebar */}
//                         {showSuccessAlert && (
//                             <Alert 
//                                 severity="success" 
//                                 sx={{ 
//                                     mb: 2, 
//                                     width: "100%", 
//                                     fontSize: "0.75rem",
//                                     '& .MuiAlert-message': {
//                                         width: '100%'
//                                     }
//                                 }}
//                                 onClose={handleCloseSuccessAlert}
//                             >
//                                 Profile picture updated successfully!
//                             </Alert>
//                         )}
                        
//                         {showErrorAlert && (
//                             <Alert 
//                                 severity="error" 
//                                 sx={{ 
//                                     mb: 2, 
//                                     width: "100%", 
//                                     fontSize: "0.75rem",
//                                     '& .MuiAlert-message': {
//                                         width: '100%'
//                                     }
//                                 }}
//                                 onClose={handleCloseErrorAlert}
//                             >
//                                 {uploadError}
//                             </Alert>
//                         )}

//                         {detailsLoading ? (
//                             <CircularProgress size={24} sx={{ color: "#fff", my: 2 }} />
//                         ) : (
//                             <>
//                                 <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700, textTransform: "capitalize" }}>
//                                     {userDisplayName || employeeName || "User"}
//                                 </Typography>
//                                 <Typography variant="subtitle2" sx={{ color: "rgba(255,255,255,0.85)", mt: 0.5 }}>
//                                     {serviceNo ? `Service No: ${serviceNo}` : "Service No: —"}
//                                 </Typography>
//                                 {(department || role) && (
//                                     <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", mt: 0.5 }}>
//                                         {department ? department : ""}{department && role ? " • " : ""}{role ? role : ""}
//                                     </Typography>
//                                 )}
//                             </>
//                         )}
//                     </Box>

//                     {/* Menu Items */}
//                     <List sx={{ mt: 4 }}>
//                         {menuItems.map((item) => (
//                             <ListItem
//                                 button
//                                 key={item.text}
//                                 onClick={() => handleNavigation(item.path)}
//                                 sx={{
//                                     my: 1,
//                                     backgroundColor: item.path === window.location.pathname ? "#E0A300" : "transparent",
//                                     color: item.path === window.location.pathname ? "white" : "white",
//                                     borderRadius: 2,
//                                     fontWeight: "bold",
//                                     fontSize: 18,
//                                     transition: "all 0.3s ease",
//                                     "&:hover": {
//                                         backgroundColor: "#F2B807",
//                                         transform: "scale(1.05)",
//                                     },
//                                 }}
//                             >
//                                 <ListItemAvatar>
//                                     <Avatar
//                                         sx={{
//                                             color: item.path === window.location.pathname ? "white" : "white",
//                                             backgroundColor: "transparent",
//                                         }}
//                                     >
//                                         {item.icon}
//                                     </Avatar>
//                                 </ListItemAvatar>
//                                 <ListItemText primary={item.text} />
//                             </ListItem>
//                         ))}
//                     </List>
//                 </Box>
//             </Drawer>

//             {/* Image Preview Dialog */}
//             <Dialog
//                 open={openPreviewDialog}
//                 onClose={handleClosePreview}
//                 maxWidth="sm"
//                 fullWidth
//                 sx={{
//                     "& .MuiDialog-paper": {
//                         borderRadius: 2,
//                     }
//                 }}
//             >
//                 <DialogTitle sx={{ fontWeight: 700, color: "#1e0153" }}>
//                     Preview Profile Picture
//                 </DialogTitle>
//                 <DialogContent sx={{ py: 3, textAlign: "center" }}>
//                     {previewImage && (
//                         <Box>
//                             <Avatar
//                                 src={previewImage}
//                                 sx={{
//                                     width: 250,
//                                     height: 250,
//                                     mx: "auto",
//                                     mb: 2,
//                                     border: "3px solid #1e0153",
//                                 }}
//                             />
//                             <Typography variant="body2" sx={{ color: "#666", mt: 2 }}>
//                                 Does this look good? Click "Save" to update your profile picture.
//                             </Typography>
//                         </Box>
//                     )}
//                 </DialogContent>
//                 <DialogActions sx={{ p: 2, gap: 1 }}>
//                     <Button
//                         onClick={handleClosePreview}
//                         variant="outlined"
//                         sx={{
//                             color: "#999",
//                             borderColor: "#ccc",
//                             "&:hover": { borderColor: "#999" }
//                         }}
//                         disabled={uploadingImage}
//                     >
//                         Cancel
//                     </Button>
//                     <Button
//                         onClick={handleSaveImage}
//                         variant="contained"
//                         sx={{
//                             bgcolor: "#1e0153",
//                             "&:hover": { bgcolor: "#333" }
//                         }}
//                         disabled={uploadingImage}
//                     >
//                         {uploadingImage ? (
//                             <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                                 <CircularProgress size={20} sx={{ color: "white" }} />
//                                 Saving...
//                             </Box>
//                         ) : (
//                             "Save"
//                         )}
//                     </Button>
//                 </DialogActions>
//             </Dialog>

//             {/* Global Snackbar Notifications */}
//             <Snackbar 
//                 open={showSuccessAlert} 
//                 autoHideDuration={3000} 
//                 onClose={handleCloseSuccessAlert}
//                 anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
//             >
//                 <Alert onClose={handleCloseSuccessAlert} severity="success" sx={{ width: '100%' }}>
//                     Profile picture updated successfully!
//                 </Alert>
//             </Snackbar>

//             <Snackbar 
//                 open={showErrorAlert} 
//                 autoHideDuration={3000} 
//                 onClose={handleCloseErrorAlert}
//                 anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
//             >
//                 <Alert onClose={handleCloseErrorAlert} severity="error" sx={{ width: '100%' }}>
//                     {uploadError}
//                 </Alert>
//             </Snackbar>
//         </>
//     );
// };

// export default Sidebar;















// import React, { useEffect, useState } from "react";
// import {
//     Avatar,
//     Box,
//     Drawer,
//     IconButton,
//     List,
//     ListItem,
//     ListItemAvatar,
//     ListItemText,
//     Typography,
//     CircularProgress,
//     Alert,
//     Dialog,
//     DialogTitle,
//     DialogContent,
//     DialogActions,
//     Button,
//     Snackbar,
// } from "@mui/material";
// import {
//     Assignment as AssignmentIcon,
//     CalendarToday as CalendarTodayIcon,
//     Close as CloseIcon,
//     ExitToApp as ExitToAppIcon,
//     History as HistoryIcon,
//     Home as HomeIcon,
//     WorkHistory as WorkHistoryIcon,
//     MoreTime as OT,
//     ContentPaste as ContentPasteIcon,
//     Addchart as OverTime,
//     CloudUpload as CloudUploadIcon,
//     EventNote as EventNoteIcon,
// } from "@mui/icons-material";
// import { useDispatch, useSelector } from "react-redux";
// import { useNavigate } from "react-router-dom";
// import { fetchJobItems } from "../../actions/jobAction";
// import { fetchJobs } from "../../actions/AssignAction";
// import { uploadProfileImageAction, fetchUserDetails } from "../../actions/authActions";
// import Logo from "../../assets/logo6.png";
// import axios from "axios";
// import MuiAlert from '@mui/material/Alert';

// const Sidebar = ({ open, toggleDrawer }) => {
//     const dispatch = useDispatch();
//     const navigate = useNavigate();
    
//     // Get all state from Redux
//     const {
//         serviceNo,
//         userImage,
//         employeeName,
//         department,
//         role,
//         uploadingImage,
//         uploadImageError,
//         userData
//     } = useSelector((state) => state.auth || {});
    
//     // Get jobs data from Redux store
//     const { jobValue } = useSelector((state) => state.assignJob || {});
    
//     const [loadingImage, setLoadingImage] = useState(false);
//     const [imageError, setImageError] = useState(false);
//     const [userDisplayName, setUserDisplayName] = useState("");
//     const [userDisplayServiceNo, setUserDisplayServiceNo] = useState("");
//     const [detailsLoading, setDetailsLoading] = useState(false);
//     const [uploadSuccess, setUploadSuccess] = useState(false);
//     const [uploadError, setUploadError] = useState("");
//     const [showErrorAlert, setShowErrorAlert] = useState(false);
//     const [showSuccessAlert, setShowSuccessAlert] = useState(false);
//     const fileInputRef = React.useRef(null);
//     const [previewImage, setPreviewImage] = useState(null);
//     const [selectedFile, setSelectedFile] = useState(null);
//     const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
    
//     // Default image URL
//     const DEFAULT_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Ccircle cx='12' cy='8' r='4' fill='%23999'/%3E%3Cpath d='M12 14c-6 0-8 3-8 3v7h16v-7s-2-3-8-3z' fill='%23999'/%3E%3C/svg%3E";

//     // Update user image when service number changes
//     useEffect(() => {
//         if (serviceNo) {
//             setLoadingImage(true);
//             setImageError(false);
            
//             // Fetch jobs
//             const date = new Date().toISOString().split("T")[0];
//             dispatch(fetchJobs(date, serviceNo));
//         }
//     }, [serviceNo, dispatch]);

//     // Fetch user details if service number is available
//     useEffect(() => {
//         if (serviceNo && (!employeeName || !department || !role)) {
//             fetchUserDetailsFromAPI();
//         }
        
//         if (serviceNo && employeeName) {
//             setUserDisplayName(employeeName);
//             setUserDisplayServiceNo(serviceNo);
//         }
//     }, [serviceNo, employeeName, department, role]);

//     // Show error alert when uploadImageError changes
//     useEffect(() => {
//         if (uploadImageError) {
//             setUploadError(uploadImageError);
//             setShowErrorAlert(true);
//         }
//     }, [uploadImageError]);

//     // Auto-hide alerts after 3 seconds
//     useEffect(() => {
//         if (showSuccessAlert) {
//             const timer = setTimeout(() => {
//                 setShowSuccessAlert(false);
//             }, 3000);
//             return () => clearTimeout(timer);
//         }
//     }, [showSuccessAlert]);

//     useEffect(() => {
//         if (showErrorAlert) {
//             const timer = setTimeout(() => {
//                 setShowErrorAlert(false);
//             }, 3000);
//             return () => clearTimeout(timer);
//         }
//     }, [showErrorAlert]);

//     const fetchUserDetailsFromAPI = async () => {
//         if (!serviceNo) return;
        
//         setDetailsLoading(true);
//         try {
//             const res = await axios.get(
//                 `https://daladamaligawa.dockyardsoftware.com/UserDetails/GetUserDetailsByUser?sno=${serviceNo}`
//             );
            
//             console.log("User details response:", res.data);
            
//             const resultSet = res.data?.resultSet;
//             if (resultSet && Array.isArray(resultSet) && resultSet.length > 0) {
//                 const userData = resultSet[0];
//                 const name = userData?.Name || "";
//                 const dept = userData?.Department || "";
//                 const role = userData?.Designation || "";
                
//                 setUserDisplayName(name);
//                 setUserDisplayServiceNo(serviceNo);
                
//                 // Update Redux store
//                 dispatch(fetchUserDetails(serviceNo));
//             } else {
//                 setUserDisplayName(employeeName || "");
//                 setUserDisplayServiceNo(serviceNo);
//             }
//         } catch (err) {
//             console.error("Failed to fetch user details:", err);
//             setUserDisplayName(employeeName || "");
//             setUserDisplayServiceNo(serviceNo);
//         } finally {
//             setDetailsLoading(false);
//         }
//     };

//     // Handle image loading error
//     const handleImageError = () => {
//         setImageError(true);
//         setLoadingImage(false);
//     };

//     // Handle image load success
//     const handleImageLoad = () => {
//         setLoadingImage(false);
//         setImageError(false);
//     };

//     // Handle profile image selection
//     const handleProfileImageChange = (event) => {
//         const file = event.target.files[0];

//         if (file) {
//             // Validate file type - support PNG, JPEG, etc.
//             const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
//             if (!validTypes.includes(file.type)) {
//                 setUploadError("Please select a valid image file (JPEG, JPG, PNG, GIF, or WebP)");
//                 setShowErrorAlert(true);
//                 return;
//             }

//             // Validate file size (max 5MB)
//             if (file.size > 5 * 1024 * 1024) {
//                 setUploadError("Image size should not exceed 5MB");
//                 setShowErrorAlert(true);
//                 return;
//             }

//             // Create preview
//             const reader = new FileReader();
//             reader.onloadend = () => {
//                 setPreviewImage(reader.result);
//                 setSelectedFile(file);
//                 setOpenPreviewDialog(true);
//             };
//             reader.readAsDataURL(file);
//         }

//         // Reset file input
//         if (fileInputRef.current) {
//             fileInputRef.current.value = "";
//         }
//     };

//     // Handle closing preview without upload
//     const handleClosePreview = () => {
//         setOpenPreviewDialog(false);
//         setPreviewImage(null);
//         setSelectedFile(null);
//     };

//     // Handle saving/uploading the image
//     const handleSaveImage = async () => {
//         if (!selectedFile || !serviceNo) return;
        
//         try {
//             const result = await dispatch(uploadProfileImageAction(serviceNo, selectedFile));
            
//             if (result.success) {
//                 setUploadSuccess(true);
//                 setShowSuccessAlert(true);
//                 handleClosePreview();
//             } else {
//                 setUploadError(result.error || "Failed to upload image");
//                 setShowErrorAlert(true);
//             }
//         } catch (error) {
//             console.error("Image upload error:", error);
//             setUploadError("Failed to upload profile picture. Please try again.");
//             setShowErrorAlert(true);
//         }
//     };

//     // Handle closing alerts
//     const handleCloseErrorAlert = () => {
//         setShowErrorAlert(false);
//     };

//     const handleCloseSuccessAlert = () => {
//         setShowSuccessAlert(false);
//     };

//     // Define menu items
//     const allowedOTUsers = ["3000158","3000159","3000694","1004507","3001230","3000821","0001809"];
//     const isOTAllowed = allowedOTUsers.includes(String(serviceNo));

//     const baseMenuItems = [
//         { text: "Home", path: "/home", icon: <HomeIcon /> },
//         { text: "User Profile", path: "/userProfile", icon: <CalendarTodayIcon /> },
//     ];

//     const menuItems = [
//         ...baseMenuItems,
//         { text: "Shift Exchange", path: "/shiftExchange", icon: <WorkHistoryIcon /> },
//         { text: "Roster Schedule", path: "/rosterSchedule", icon: <EventNoteIcon /> },
//         ...(isOTAllowed
//             ? [{ text: "Management Info", path: "/overTimeView", icon: <OverTime /> }]
//             : []),
//         ...(jobValue && jobValue.length > 0 ? [
//             { text: "Assign OT", path: "/assignOT", icon: <OT /> },
//             { text: "Holiday Assign OT", path: "/holidayOT", icon: <OT /> },
//             { text: "OT Details", path: "/otDetails", icon: <ContentPasteIcon /> },
//         ] : []),
//         { text: "Log Out", path: "/logout", icon: <ExitToAppIcon /> },
//     ];

//     const handleNavigation = (path) => {
//         if (path === "/jobHistory" || path === "/jobHistoryAll") {
//             dispatch(fetchJobItems(new Date().toISOString().split("T")[0], serviceNo, role));
//         }
//         if (path === "/logout") {
//             localStorage.clear();
//             navigate("/");
//         } else if (path === "/rosterSchedule") {
//             navigate(path);
//         } else {
//             navigate(path, {
//                 state: { employeeId: serviceNo, department, role },
//             });
//         }
//         toggleDrawer(false);
//     };

//     // Get image source
//     const imageSource = userImage || 
//         (serviceNo ? `https://daladamaligawa.dockyardsoftware.com/User/GetUserImg?Sno=${serviceNo}` : DEFAULT_IMAGE);

//     return (
//         <>
//             <Drawer
//                 open={open}
//                 onClose={() => toggleDrawer(false)}
//                 anchor="left"
//                 sx={{
//                     "& .MuiDrawer-paper": {
//                         width: 280,
//                         bgcolor: "transparent",
//                         borderRight: "none",
//                         transition: "transform 0.3s ease",
//                         transform: open ? "translateX(0)" : "translateX(-100%)",
//                         boxShadow: "0 0 15px rgba(0, 0, 0, 0.5)",
//                         borderTopRightRadius: 40,
//                         borderBottomRightRadius: 40,
//                         backdropFilter: "blur(10px)",
//                     },
//                 }}
//             >
//                 <Box
//                     sx={{
//                         display: "flex",
//                         flexDirection: "column",
//                         height: "100%",
//                         px: 2,
//                         py: 3,
//                         color: "white",
//                     }}
//                 >
//                     {/* Header Section */}
//                     <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
//                         <img
//                             src={Logo}
//                             alt="Logo"
//                             style={{
//                                 height: 70,
//                                 objectFit: "contain",
//                                 borderRadius: "5px",
//                             }}
//                         />
//                         <IconButton onClick={() => toggleDrawer(false)} sx={{ color: "white" }}>
//                             <CloseIcon />
//                         </IconButton>
//                     </Box>
                    
//                     {/* Profile Section */}
//                     <Box
//                         sx={{
//                             display: "flex",
//                             flexDirection: "column",
//                             alignItems: "center",
//                             justifyContent: "center",
//                             mt: 2,
//                             textAlign: "center",
//                             width: "100%",
//                         }}
//                     >
//                         <Box sx={{ position: "relative", mb: 2 }}>
//                             {loadingImage && (
//                                 <CircularProgress
//                                     sx={{
//                                         position: "absolute",
//                                         top: "50%",
//                                         left: "50%",
//                                         marginTop: "-65px",
//                                         marginLeft: "-65px",
//                                         color: "#ddd"
//                                     }}
//                                     size={130}
//                                 />
//                             )}
//                             <Box
//                                 onClick={() => fileInputRef.current?.click()}
//                                 sx={{
//                                     position: "relative",
//                                     cursor: uploadingImage ? "not-allowed" : "pointer",
//                                     opacity: uploadingImage ? 0.6 : 1,
//                                     transition: "all 0.3s ease",
//                                     "&:hover": {
//                                         filter: uploadingImage ? "none" : "brightness(0.9)",
//                                         transform: uploadingImage ? "none" : "scale(1.05)",
//                                     },
//                                 }}
//                             >
//                                 <Avatar
//                                     src={imageError ? DEFAULT_IMAGE : imageSource}
//                                     onError={handleImageError}
//                                     onLoad={handleImageLoad}
//                                     sx={{
//                                         width: 130,
//                                         height: 130,
//                                         border: "3px solid #ddd",
//                                         backgroundColor: "#f0f0f0",
//                                     }}
//                                 />
//                                 {!uploadingImage && (
//                                     <Box
//                                         sx={{
//                                             position: "absolute",
//                                             bottom: 5,
//                                             right: 5,
//                                             backgroundColor: "#1e0153",
//                                             borderRadius: "50%",
//                                             padding: "8px",
//                                             display: "flex",
//                                             alignItems: "center",
//                                             justifyContent: "center",
//                                             color: "white",
//                                             boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
//                                         }}
//                                     >
//                                         <CloudUploadIcon sx={{ fontSize: 20 }} />
//                                     </Box>
//                                 )}
//                             </Box>

//                             <input
//                                 ref={fileInputRef}
//                                 type="file"
//                                 accept="image/*"
//                                 onChange={handleProfileImageChange}
//                                 style={{ display: "none" }}
//                                 disabled={uploadingImage}
//                             />
//                         </Box>

//                         {/* Success/Error Messages inside Sidebar */}
//                         {showSuccessAlert && (
//                             <Alert 
//                                 severity="success" 
//                                 sx={{ 
//                                     mb: 2, 
//                                     width: "100%", 
//                                     fontSize: "0.75rem",
//                                     '& .MuiAlert-message': {
//                                         width: '100%'
//                                     }
//                                 }}
//                                 onClose={handleCloseSuccessAlert}
//                             >
//                                 Profile picture updated successfully!
//                             </Alert>
//                         )}
                        
//                         {showErrorAlert && (
//                             <Alert 
//                                 severity="error" 
//                                 sx={{ 
//                                     mb: 2, 
//                                     width: "100%", 
//                                     fontSize: "0.75rem",
//                                     '& .MuiAlert-message': {
//                                         width: '100%'
//                                     }
//                                 }}
//                                 onClose={handleCloseErrorAlert}
//                             >
//                                 {uploadError}
//                             </Alert>
//                         )}

//                         {detailsLoading ? (
//                             <CircularProgress size={24} sx={{ color: "#fff", my: 2 }} />
//                         ) : (
//                             <>
//                                 <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700, textTransform: "capitalize" }}>
//                                     {userDisplayName || employeeName || "User"}
//                                 </Typography>
//                                 <Typography variant="subtitle2" sx={{ color: "rgba(255,255,255,0.85)", mt: 0.5 }}>
//                                     {serviceNo ? `Service No: ${serviceNo}` : "Service No: —"}
//                                 </Typography>
//                                 {(department || role) && (
//                                     <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", mt: 0.5 }}>
//                                         {department ? department : ""}{department && role ? " • " : ""}{role ? role : ""}
//                                     </Typography>
//                                 )}
//                             </>
//                         )}
//                     </Box>

//                     {/* Menu Items */}
//                     <List sx={{ mt: 4 }}>
//                         {menuItems.map((item) => (
//                             <ListItem
//                                 button
//                                 key={item.text}
//                                 onClick={() => handleNavigation(item.path)}
//                                 sx={{
//                                     my: 1,
//                                     backgroundColor: item.path === window.location.pathname ? "#E0A300" : "transparent",
//                                     color: item.path === window.location.pathname ? "white" : "white",
//                                     borderRadius: 2,
//                                     fontWeight: "bold",
//                                     fontSize: 18,
//                                     transition: "all 0.3s ease",
//                                     "&:hover": {
//                                         backgroundColor: "#F2B807",
//                                         transform: "scale(1.05)",
//                                     },
//                                 }}
//                             >
//                                 <ListItemAvatar>
//                                     <Avatar
//                                         sx={{
//                                             color: item.path === window.location.pathname ? "white" : "white",
//                                             backgroundColor: "transparent",
//                                         }}
//                                     >
//                                         {item.icon}
//                                     </Avatar>
//                                 </ListItemAvatar>
//                                 <ListItemText primary={item.text} />
//                             </ListItem>
//                         ))}
//                     </List>
//                 </Box>
//             </Drawer>

//             {/* Image Preview Dialog */}
//             <Dialog
//                 open={openPreviewDialog}
//                 onClose={handleClosePreview}
//                 maxWidth="sm"
//                 fullWidth
//                 sx={{
//                     "& .MuiDialog-paper": {
//                         borderRadius: 2,
//                     }
//                 }}
//             >
//                 <DialogTitle sx={{ fontWeight: 700, color: "#1e0153" }}>
//                     Preview Profile Picture
//                 </DialogTitle>
//                 <DialogContent sx={{ py: 3, textAlign: "center" }}>
//                     {previewImage && (
//                         <Box>
//                             <Avatar
//                                 src={previewImage}
//                                 sx={{
//                                     width: 250,
//                                     height: 250,
//                                     mx: "auto",
//                                     mb: 2,
//                                     border: "3px solid #1e0153",
//                                 }}
//                             />
//                             <Typography variant="body2" sx={{ color: "#666", mt: 2 }}>
//                                 Does this look good? Click "Save" to update your profile picture.
//                             </Typography>
//                         </Box>
//                     )}
//                 </DialogContent>
//                 <DialogActions sx={{ p: 2, gap: 1 }}>
//                     <Button
//                         onClick={handleClosePreview}
//                         variant="outlined"
//                         sx={{
//                             color: "#999",
//                             borderColor: "#ccc",
//                             "&:hover": { borderColor: "#999" }
//                         }}
//                         disabled={uploadingImage}
//                     >
//                         Cancel
//                     </Button>
//                     <Button
//                         onClick={handleSaveImage}
//                         variant="contained"
//                         sx={{
//                             bgcolor: "#1e0153",
//                             "&:hover": { bgcolor: "#333" }
//                         }}
//                         disabled={uploadingImage}
//                     >
//                         {uploadingImage ? (
//                             <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                                 <CircularProgress size={20} sx={{ color: "white" }} />
//                                 Saving...
//                             </Box>
//                         ) : (
//                             "Save"
//                         )}
//                     </Button>
//                 </DialogActions>
//             </Dialog>

//             {/* Global Snackbar Notifications */}
//             <Snackbar 
//                 open={showSuccessAlert} 
//                 autoHideDuration={3000} 
//                 onClose={handleCloseSuccessAlert}
//                 anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
//             >
//                 <Alert onClose={handleCloseSuccessAlert} severity="success" sx={{ width: '100%' }}>
//                     Profile picture updated successfully!
//                 </Alert>
//             </Snackbar>

//             <Snackbar 
//                 open={showErrorAlert} 
//                 autoHideDuration={3000} 
//                 onClose={handleCloseErrorAlert}
//                 anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
//             >
//                 <Alert onClose={handleCloseErrorAlert} severity="error" sx={{ width: '100%' }}>
//                     {uploadError}
//                 </Alert>
//             </Snackbar>
//         </>
//     );
// };

// export default Sidebar;













// import React, { useEffect, useState } from "react";
// import {
//     Avatar,
//     Box,
//     Drawer,
//     IconButton,
//     List,
//     ListItem,
//     ListItemAvatar,
//     ListItemText,
//     Typography,
//     CircularProgress,
//     Alert,
//     Dialog,
//     DialogTitle,
//     DialogContent,
//     DialogActions,
//     Button,
// } from "@mui/material";
// import {
//     Assignment as AssignmentIcon,
//     CalendarToday as CalendarTodayIcon,
//     Close as CloseIcon,
//     ExitToApp as ExitToAppIcon,
//     History as HistoryIcon,
//     Home as HomeIcon,
//     WorkHistory as WorkHistoryIcon,
//     MoreTime as OT,
//     ContentPaste as ContentPasteIcon,
//     Addchart as OverTime,
//     CloudUpload as CloudUploadIcon,
// } from "@mui/icons-material";
// import { useDispatch, useSelector } from "react-redux";
// import { useNavigate } from "react-router-dom";
// import { fetchJobItems } from "../../actions/jobAction";
// import { fetchUserData } from "../../service/authService";
// import { fetchJobs } from "../../actions/AssignAction";
// import Logo from "../../assets/logo6.png";
// import axios from "axios";
// const Sidebar = ({ open, toggleDrawer }) => {
//     const dispatch = useDispatch();
//     const navigate = useNavigate();
//     const [phoneNumber, setPhoneNumber] = useState(localStorage.getItem("phoneNumber") || "");
//     const [employeeId, setEmployeeId] = useState(localStorage.getItem("employeeId") || localStorage.getItem("serviceNo") || "");
//     const [employeeName, setEmployeeName] = useState(localStorage.getItem("employeeName") || "");
//     const [department, setDepartment] = useState(localStorage.getItem("department") || "");
//     const [role, setRole] = useState(localStorage.getItem("role") || "");
//     const [userImg, setUserImg] = useState("");
//     const [loadingImage, setLoadingImage] = useState(false);
//     const [imageError, setImageError] = useState(false);
//     const [userDisplayName, setUserDisplayName] = useState("");
//     const [userDisplayServiceNo, setUserDisplayServiceNo] = useState("");
//     const [detailsLoading, setDetailsLoading] = useState(false);
//     const [uploadingImage, setUploadingImage] = useState(false);
//     const [uploadSuccess, setUploadSuccess] = useState(false);
//     const [uploadError, setUploadError] = useState("");
//     const fileInputRef = React.useRef(null);
//     const [previewImage, setPreviewImage] = useState(null);
//     const [selectedFile, setSelectedFile] = useState(null);
//     const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
    
//     // Default image URL
//     const DEFAULT_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Ccircle cx='12' cy='8' r='4' fill='%23999'/%3E%3Cpath d='M12 14c-6 0-8 3-8 3v7h16v-7s-2-3-8-3z' fill='%23999'/%3E%3C/svg%3E";
    
//     // Get jobs data from Redux store
//     const { jobValue } = useSelector((state) => state.assignJob || {});
//     // Update userImg dynamically when employeeId changes
//     useEffect(() => {
//         if (employeeId) {
//             setLoadingImage(true);
//             setImageError(false);
//             // Use the provided API endpoint with service number
//             setUserImg(`https://daladamaligawa.dockyardsoftware.com/User/GetUserImg?Sno=${employeeId}`);
//         }
//         const date = new Date().toISOString().split("T")[0];
//         dispatch(fetchJobs(date, employeeId));
//     }, [employeeId]);
    


//     useEffect(() => {
//         if (phoneNumber && !employeeId) {
//             const fetchUserDataAsync = async () => {
//                 try {
//                     const response = await fetchUserData(phoneNumber);
                    
//                     // Check both responceType and responceMessage
//                     if (response?.responceType === true && response?.responceMessage) {
//                         const [empId, empName, dept, role] = response.responceMessage.split("/");
                        
//                         setEmployeeId(empId);
//                         setEmployeeName(empName);
//                         setDepartment(dept);
//                         setRole(role);
                        
//                         localStorage.setItem("employeeId", empId);
//                         localStorage.setItem("employeeName", empName);
//                         localStorage.setItem("department", dept);
//                         localStorage.setItem("role", role);
//                         localStorage.setItem("correctPhoneFormat", response.correctPhoneFormat);
//                     }
//                 } catch (error) {
//                     console.error("Failed to fetch user data:", error);
//                 }
//             };
//             fetchUserDataAsync();
//         }
//     }, [phoneNumber, employeeId]);

//     // Fetch professional user details (name + id) when service number available
//     useEffect(() => {
//         const svc = localStorage.getItem("serviceNo") || employeeId;
//         if (!svc) return;

//         setDetailsLoading(true);
//         axios.get(`https://daladamaligawa.dockyardsoftware.com/UserDetails/GetUserDetailsByUser?sno=${svc}`)
//             .then(res => {
//                 console.log("User details response:", res.data);
                
//                 // API returns { statusCode, result, resultSet: [...] }
//                 const resultSet = res.data?.resultSet;
//                 if (resultSet && Array.isArray(resultSet) && resultSet.length > 0) {
//                     const userData = resultSet[0];
//                     const name = userData?.Name || "";
//                     console.log("Fetched name:", name);
//                     setUserDisplayName(name);
//                 } else {
//                     setUserDisplayName(employeeName || "");
//                 }
//                 setUserDisplayServiceNo(svc);
//             })
//             .catch(err => {
//                 console.error("Failed to fetch user details:", err);
//                 setUserDisplayName(employeeName || "");
//                 setUserDisplayServiceNo(svc);
//             })
//             .finally(() => setDetailsLoading(false));
//     }, [employeeId, employeeName]);

    

//     // Handle image loading error - show default image
//     const handleImageError = () => {
//         setImageError(true);
//         setLoadingImage(false);
//     };

//     // Handle image load success
//     const handleImageLoad = () => {
//         setLoadingImage(false);
//         setImageError(false);
//     };

//     // Handle profile image selection and upload
//     const handleProfileImageChange = async (event) => {
//         const file = event.target.files[0];

//         if (file) {
//             // Validate file type
//             const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
//             if (!validTypes.includes(file.type)) {
//                 setUploadError("Please select a valid image file (JPEG, PNG, GIF, or WebP)");
//                 setTimeout(() => setUploadError(""), 3000);
//                 return;
//             }

//             // Validate file size (max 5MB)
//             if (file.size > 5 * 1024 * 1024) {
//                 setUploadError("Image size should not exceed 5MB");
//                 setTimeout(() => setUploadError(""), 3000);
//                 return;
//             }

//             // Create preview
//             const reader = new FileReader();
//             reader.onloadend = () => {
//                 setPreviewImage(reader.result);
//                 setSelectedFile(file);
//                 setOpenPreviewDialog(true);
//             };
//             reader.readAsDataURL(file);
//         }

//         // Reset file input
//         if (fileInputRef.current) {
//             fileInputRef.current.value = "";
//         }
//     };

//     // Handle closing preview without upload
//     const handleClosePreview = () => {
//         setOpenPreviewDialog(false);
//         setPreviewImage(null);
//         setSelectedFile(null);
//         setUploadError("");
//     };

//     // Handle saving/uploading the image
//     const handleSaveImage = async () => {
//         if (!selectedFile) return;
        
//         // Get employeeId from multiple possible localStorage keys
//         const currentEmployeeId = localStorage.getItem("employeeId") 
//             || localStorage.getItem("serviceNo") 
//             || employeeId;
        
//         if (!currentEmployeeId) {
//             setUploadError("Employee ID not found. Please login again.");
//             setTimeout(() => setUploadError(""), 3000);
//             return;
//         }
        
//         setUploadingImage(true);
//         setUploadError("");
//         setUploadSuccess(false);

//         const formDataForUpload = new FormData();
//         formDataForUpload.append("ImageFile", selectedFile);
//         formDataForUpload.append("ServiceNo", currentEmployeeId);

//         console.log("Uploading image for employee:", currentEmployeeId);
//         console.log("File details:", selectedFile.name, selectedFile.type, selectedFile.size);

//         try {
//             // Using fetch to avoid CORS header issues
//             const response = await fetch(
//                 `https://daladamaligawa.dockyardsoftware.com/User/Fileupload?ServiceNo=${currentEmployeeId}`,
//                 {
//                     method: "POST",
//                     body: formDataForUpload,
//                 }
//             );

//             console.log("Upload response status:", response.status);
//             const responseData = await response.text();
//             console.log("Upload response data:", responseData);

//             if (!response.ok) {
//                 throw new Error(responseData || "Failed to upload image");
//             }

//             // Refresh the image by adding a timestamp to bypass cache
//             const timestamp = new Date().getTime();
//             setUserImg(
//                 `https://daladamaligawa.dockyardsoftware.com/User/GetUserImg?Sno=${currentEmployeeId}&t=${timestamp}`
//             );
//             setImageError(false);

//             setUploadSuccess(true);
//             handleClosePreview();
//             setTimeout(() => setUploadSuccess(false), 3000);
//         } catch (error) {
//             console.error("Image upload error:", error);
//             setUploadError("Failed to upload profile picture. Please try again.");
//             setTimeout(() => setUploadError(""), 3000);
//         } finally {
//             setUploadingImage(false);
//         }
//     };

//     // Define base menu items 
//     const allowedOTUsers = ["3000158","3000159","3000694","1004507","3001230","3000821","0001809"];
//     const isOTAllowed = allowedOTUsers.includes(String(employeeId));

//     const baseMenuItems = [
//         { text: "Home", path: "/home", icon: <HomeIcon /> },
//         { text: "User Profile", path: "/todayAttendance", icon: <CalendarTodayIcon /> },
//         // { text: "Attendance Jobs History", path: "/jobHistory", icon: <HistoryIcon /> },
//         // { text: "All Attendance Jobs History", path: "/jobHistoryAll", icon: <WorkHistoryIcon /> },
//         // { text: "Assign Jobs", path: "/assignJob", icon: <AssignmentIcon /> },
//         // { text: "Over time", path: "/overTimeView", icon: < OT/> },
//         ...(isOTAllowed
//     ? [{ text: "Management Info", path: "/overTimeView", icon: <OverTime /> }]
//     : []),
//     ];
//     // Add "Assign OT" only if jobs are available
//     const menuItems = [
//         ...baseMenuItems,
//         ...(jobValue && jobValue.length > 0 ? [{ text: "Assign OT", path: "/assignOT", icon: <OT /> },
//             { text: "Holiday Assign OT", path: "/holidayOT", icon: <OT /> },
//             { text: "OT Details", path: "/otDetails", icon: <ContentPasteIcon /> },
            
//         ] : []),
//         { text: "Log Out", path: "/logout", icon: <ExitToAppIcon /> },
//     ];
//     const handleNavigation = (path) => {
//         if (path === "/jobHistory" || path === "/jobHistoryAll") {
//             dispatch(fetchJobItems(new Date().toISOString().split("T")[0], employeeId, role));
//         }
//         if (path === "/logout") {
//             localStorage.clear();
//             navigate("/");
//         } else {
//             navigate(path, {
//                 state: { employeeId, department, role },
//             });
//         }
//         toggleDrawer(false);
//     };
//     // Rest of the component remains the same...
//     return (
//         <>
//         <Drawer
//             open={open}
//             onClose={() => toggleDrawer(false)}
//             anchor="left"
//             sx={{
//                 "& .MuiDrawer-paper": {
//                     width: 280,
//                     bgcolor: "transparent",
//                     borderRight: "none",
//                     transition: "transform 0.3s ease",
//                     transform: open ? "translateX(0)" : "translateX(-100%)",
//                     boxShadow: "0 0 15px rgba(0, 0, 0, 0.5)",
//                     borderTopRightRadius: 40,
//                     borderBottomRightRadius: 40,
//                     backdropFilter: "blur(10px)",
//                 },
//             }}
//         >
//             <Box
//                 sx={{
//                     display: "flex",
//                     flexDirection: "column",
//                     height: "100%",
//                     px: 2,
//                     py: 3,
//                     color: "white",
//                 }}
//             >
//                 {/* Header Section */}
//                 <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
//                     <img
//                         src={Logo}
//                         alt="Logo"
//                         style={{
//                             height: 70,
//                             objectFit: "contain",
//                             borderRadius: "5px",
//                         }}
//                     />
//                     <IconButton onClick={() => toggleDrawer(false)} sx={{ color: "white" }}>
//                         <CloseIcon />
//                     </IconButton>
//                 </Box>
//                 <Box
//                     sx={{
//                         display: "flex",
//                         flexDirection: "column",
//                         alignItems: "center",
//                         justifyContent: "center",
//                         mt: 2,
//                         textAlign: "center",
//                         width: "100%",
//                     }}
//                 >
//                     <Box sx={{ position: "relative", mb: 2 }}>
//                         {loadingImage && (
//                             <CircularProgress
//                                 sx={{
//                                     position: "absolute",
//                                     top: "50%",
//                                     left: "50%",
//                                     marginTop: "-65px",
//                                     marginLeft: "-65px",
//                                     color: "#ddd"
//                                 }}
//                                 size={130}
//                             />
//                         )}
//                         <Box
//                             onClick={() => fileInputRef.current?.click()}
//                             sx={{
//                                 position: "relative",
//                                 cursor: uploadingImage ? "not-allowed" : "pointer",
//                                 opacity: uploadingImage ? 0.6 : 1,
//                                 transition: "all 0.3s ease",
//                                 "&:hover": {
//                                     filter: uploadingImage ? "none" : "brightness(0.9)",
//                                     transform: uploadingImage ? "none" : "scale(1.05)",
//                                 },
//                             }}
//                         >
//                             <Avatar
//                                 src={imageError ? DEFAULT_IMAGE : userImg}
//                                 onError={handleImageError}
//                                 onLoad={handleImageLoad}
//                                 sx={{
//                                     width: 130,
//                                     height: 130,
//                                     border: "3px solid #ddd",
//                                     backgroundColor: "#f0f0f0",
//                                 }}
//                             />
//                             {!uploadingImage && (
//                                 <Box
//                                     sx={{
//                                         position: "absolute",
//                                         bottom: 5,
//                                         right: 5,
//                                         backgroundColor: "#1e0153",
//                                         borderRadius: "50%",
//                                         padding: "8px",
//                                         display: "flex",
//                                         alignItems: "center",
//                                         justifyContent: "center",
//                                         color: "white",
//                                         boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
//                                     }}
//                                 >
//                                     <CloudUploadIcon sx={{ fontSize: 20 }} />
//                                 </Box>
//                             )}
//                         </Box>

//                         <input
//                             ref={fileInputRef}
//                             type="file"
//                             accept="image/*"
//                             onChange={handleProfileImageChange}
//                             style={{ display: "none" }}
//                             disabled={uploadingImage}
//                         />
//                     </Box>

//                     {/* Success/Error Messages */}
//                     {uploadSuccess && (
//                         <Alert severity="success" sx={{ mb: 2, width: "100%", fontSize: "0.75rem" }}>
//                             Profile picture updated!
//                         </Alert>
//                     )}
//                     {uploadError && (
//                         <Alert severity="error" sx={{ mb: 2, width: "100%", fontSize: "0.75rem" }}>
//                             {uploadError}
//                         </Alert>
//                     )}

//                     <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700, textTransform: "capitalize" }}>
//                         {userDisplayName || employeeName || "User"}
//                     </Typography>
//                     <Typography variant="subtitle2" sx={{ color: "rgba(255,255,255,0.85)", mt: 0.5 }}>
//                         {userDisplayServiceNo || employeeId ? `Service No: ${userDisplayServiceNo || employeeId}` : "Service No: —"}
//                     </Typography>
//                     {(department || role) && (
//                         <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", mt: 0.5 }}>
//                             {department ? department : ""}{department && role ? " • " : ""}{role ? role : ""}
//                         </Typography>
//                     )}
//                 </Box>
//                 {/* Menu Items */}
//                 <List sx={{ mt: 4 }}>
//                     {menuItems.map((item) => (
//                         <ListItem
//                             button
//                             key={item.text}
//                             onClick={() => handleNavigation(item.path)}
//                             sx={{
//                                 my: 1,
//                                 backgroundColor: item.path === window.location.pathname ? "#E0A300" : "transparent",
//                                 color: item.path === window.location.pathname ? "white" : "white",
//                                 borderRadius: 2,
//                                 fontWeight: "bold",
//                                 fontSize: 18,
//                                 transition: "all 0.3s ease",
//                                 "&:hover": {
//                                     backgroundColor: "#F2B807",
//                                     transform: "scale(1.05)",
//                                 },
//                             }}
//                         >
//                             <ListItemAvatar>
//                                 <Avatar
//                                     sx={{
//                                         color: item.path === window.location.pathname ? "white" : "white",
//                                         backgroundColor: "transparent",
//                                     }}
//                                 >
//                                     {item.icon}
//                                 </Avatar>
//                             </ListItemAvatar>
//                             <ListItemText primary={item.text} />
//                         </ListItem>
//                     ))}
//                 </List>
//             </Box>
//         </Drawer>

//         {/* Image Preview Dialog */}
//         <Dialog
//             open={openPreviewDialog}
//             onClose={handleClosePreview}
//             maxWidth="sm"
//             fullWidth
//             sx={{
//                 "& .MuiDialog-paper": {
//                     borderRadius: 2,
//                 }
//             }}
//         >
//             <DialogTitle sx={{ fontWeight: 700, color: "#1e0153" }}>
//                 Preview Profile Picture
//             </DialogTitle>
//             <DialogContent sx={{ py: 3, textAlign: "center" }}>
//                 {previewImage && (
//                     <Box>
//                         <Avatar
//                             src={previewImage}
//                             sx={{
//                                 width: 250,
//                                 height: 250,
//                                 mx: "auto",
//                                 mb: 2,
//                                 border: "3px solid #1e0153",
//                             }}
//                         />
//                         <Typography variant="body2" sx={{ color: "#666", mt: 2 }}>
//                             Does this look good? Click "Save" to update your profile picture.
//                         </Typography>
//                     </Box>
//                 )}
//             </DialogContent>
//             <DialogActions sx={{ p: 2, gap: 1 }}>
//                 <Button
//                     onClick={handleClosePreview}
//                     variant="outlined"
//                     sx={{
//                         color: "#999",
//                         borderColor: "#ccc",
//                         "&:hover": { borderColor: "#999" }
//                     }}
//                     disabled={uploadingImage}
//                 >
//                     Cancel
//                 </Button>
//                 <Button
//                     onClick={handleSaveImage}
//                     variant="contained"
//                     sx={{
//                         bgcolor: "#1e0153",
//                         "&:hover": { bgcolor: "#333" }
//                     }}
//                     disabled={uploadingImage}
//                 >
//                     {uploadingImage ? (
//                         <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                             <CircularProgress size={20} sx={{ color: "white" }} />
//                             Saving...
//                         </Box>
//                     ) : (
//                         "Save"
//                     )}
//                 </Button>
//             </DialogActions>
//         </Dialog>
//         </>
//     );
// };
// export default Sidebar;














// import React, { useEffect, useState } from "react";
// import {
//     Avatar,
//     Box,
//     Drawer,
//     IconButton,
//     List,
//     ListItem,
//     ListItemAvatar,
//     ListItemText,
//     Typography,
//     CircularProgress,
//     Alert,
//     Dialog,
//     DialogTitle,
//     DialogContent,
//     DialogActions,
//     Button,
// } from "@mui/material";
// import {
//     Assignment as AssignmentIcon,
//     CalendarToday as CalendarTodayIcon,
//     Close as CloseIcon,
//     ExitToApp as ExitToAppIcon,
//     History as HistoryIcon,
//     Home as HomeIcon,
//     WorkHistory as WorkHistoryIcon,
//     MoreTime as OT,
//     ContentPaste as ContentPasteIcon,
//     Addchart as OverTime,
//     CloudUpload as CloudUploadIcon,
// } from "@mui/icons-material";
// import { useDispatch, useSelector } from "react-redux";
// import { useNavigate } from "react-router-dom";
// import { fetchJobItems } from "../../actions/jobAction";
// import { fetchUserData } from "../../service/authService";
// import { fetchJobs } from "../../actions/AssignAction";
// import Logo from "../../assets/logo6.png";
// import axios from "axios";
// const Sidebar = ({ open, toggleDrawer }) => {
//     const dispatch = useDispatch();
//     const navigate = useNavigate();
//     const [phoneNumber, setPhoneNumber] = useState(localStorage.getItem("phoneNumber") || "");
//     const [employeeId, setEmployeeId] = useState(localStorage.getItem("employeeId") || localStorage.getItem("serviceNo") || "");
//     const [employeeName, setEmployeeName] = useState(localStorage.getItem("employeeName") || "");
//     const [department, setDepartment] = useState(localStorage.getItem("department") || "");
//     const [role, setRole] = useState(localStorage.getItem("role") || "");
//     const [userImg, setUserImg] = useState("");
//     const [loadingImage, setLoadingImage] = useState(false);
//     const [imageError, setImageError] = useState(false);
//     const [uploadingImage, setUploadingImage] = useState(false);
//     const [uploadSuccess, setUploadSuccess] = useState(false);
//     const [uploadError, setUploadError] = useState("");
//     const fileInputRef = React.useRef(null);
//     const [previewImage, setPreviewImage] = useState(null);
//     const [selectedFile, setSelectedFile] = useState(null);
//     const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
    
//     // Default image URL
//     const DEFAULT_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Ccircle cx='12' cy='8' r='4' fill='%23999'/%3E%3Cpath d='M12 14c-6 0-8 3-8 3v7h16v-7s-2-3-8-3z' fill='%23999'/%3E%3C/svg%3E";
    
//     // Get jobs data from Redux store
//     const { jobValue } = useSelector((state) => state.assignJob || {});
//     // Update userImg dynamically when employeeId changes
//     useEffect(() => {
//         if (employeeId) {
//             setLoadingImage(true);
//             setImageError(false);
//             // Use the provided API endpoint with service number
//             setUserImg(`https://daladamaligawa.dockyardsoftware.com/User/GetUserImg?Sno=${employeeId}`);
//         }
//         const date = new Date().toISOString().split("T")[0];
//         dispatch(fetchJobs(date, employeeId));
//     }, [employeeId]);
    


//     useEffect(() => {
//         if (phoneNumber && !employeeId) {
//             const fetchUserDataAsync = async () => {
//                 try {
//                     const response = await fetchUserData(phoneNumber);
                    
//                     // Check both responceType and responceMessage
//                     if (response?.responceType === true && response?.responceMessage) {
//                         const [empId, empName, dept, role] = response.responceMessage.split("/");
                        
//                         setEmployeeId(empId);
//                         setEmployeeName(empName);
//                         setDepartment(dept);
//                         setRole(role);
                        
//                         localStorage.setItem("employeeId", empId);
//                         localStorage.setItem("employeeName", empName);
//                         localStorage.setItem("department", dept);
//                         localStorage.setItem("role", role);
//                         localStorage.setItem("correctPhoneFormat", response.correctPhoneFormat);
//                     }
//                 } catch (error) {
//                     console.error("Failed to fetch user data:", error);
//                 }
//             };
//             fetchUserDataAsync();
//         }
//     }, [phoneNumber, employeeId]);

//    console.log("Employee ID value:", employeeId);

//     // Handle image loading error - show default image
//     const handleImageError = () => {
//         setImageError(true);
//         setLoadingImage(false);
//     };

//     // Handle image load success
//     const handleImageLoad = () => {
//         setLoadingImage(false);
//         setImageError(false);
//     };

//     // Handle profile image selection and upload
//     const handleProfileImageChange = async (event) => {
//         const file = event.target.files[0];

//         if (file) {
//             // Validate file type
//             const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
//             if (!validTypes.includes(file.type)) {
//                 setUploadError("Please select a valid image file (JPEG, PNG, GIF, or WebP)");
//                 setTimeout(() => setUploadError(""), 3000);
//                 return;
//             }

//             // Validate file size (max 5MB)
//             if (file.size > 5 * 1024 * 1024) {
//                 setUploadError("Image size should not exceed 5MB");
//                 setTimeout(() => setUploadError(""), 3000);
//                 return;
//             }

//             // Create preview
//             const reader = new FileReader();
//             reader.onloadend = () => {
//                 setPreviewImage(reader.result);
//                 setSelectedFile(file);
//                 setOpenPreviewDialog(true);
//             };
//             reader.readAsDataURL(file);
//         }

//         // Reset file input
//         if (fileInputRef.current) {
//             fileInputRef.current.value = "";
//         }
//     };

//     // Handle closing preview without upload
//     const handleClosePreview = () => {
//         setOpenPreviewDialog(false);
//         setPreviewImage(null);
//         setSelectedFile(null);
//         setUploadError("");
//     };

//     // Handle saving/uploading the image
//     const handleSaveImage = async () => {
//         if (!selectedFile) return;
        
//         // Get employeeId from multiple possible localStorage keys
//         const currentEmployeeId = localStorage.getItem("employeeId") 
//             || localStorage.getItem("serviceNo") 
//             || employeeId;
        
//         if (!currentEmployeeId) {
//             setUploadError("Employee ID not found. Please login again.");
//             setTimeout(() => setUploadError(""), 3000);
//             return;
//         }
        
//         setUploadingImage(true);
//         setUploadError("");
//         setUploadSuccess(false);

//         const formDataForUpload = new FormData();
//         formDataForUpload.append("ImageFile", selectedFile);
//         formDataForUpload.append("ServiceNo", currentEmployeeId);

//         console.log("Uploading image for employee:", currentEmployeeId);
//         console.log("File details:", selectedFile.name, selectedFile.type, selectedFile.size);

//         try {
//             // Using fetch to avoid CORS header issues
//             const response = await fetch(
//                 `https://daladamaligawa.dockyardsoftware.com/User/Fileupload?ServiceNo=${currentEmployeeId}`,
//                 {
//                     method: "POST",
//                     body: formDataForUpload,
//                 }
//             );

//             console.log("Upload response status:", response.status);
//             const responseData = await response.text();
//             console.log("Upload response data:", responseData);

//             if (!response.ok) {
//                 throw new Error(responseData || "Failed to upload image");
//             }

//             // Refresh the image by adding a timestamp to bypass cache
//             const timestamp = new Date().getTime();
//             setUserImg(
//                 `https://daladamaligawa.dockyardsoftware.com/User/GetUserImg?Sno=${currentEmployeeId}&t=${timestamp}`
//             );
//             setImageError(false);

//             setUploadSuccess(true);
//             handleClosePreview();
//             setTimeout(() => setUploadSuccess(false), 3000);
//         } catch (error) {
//             console.error("Image upload error:", error);
//             setUploadError("Failed to upload profile picture. Please try again.");
//             setTimeout(() => setUploadError(""), 3000);
//         } finally {
//             setUploadingImage(false);
//         }
//     };

//     // Define base menu items 
//     const allowedOTUsers = ["3000158","3000159","3000694","1004507","3001230","3000821","0001809"];
//     const isOTAllowed = allowedOTUsers.includes(String(employeeId));

//     const baseMenuItems = [
//         { text: "Home", path: "/home", icon: <HomeIcon /> },
//         { text: "Today Attendance", path: "/todayAttendance", icon: <CalendarTodayIcon /> },
//         // { text: "Attendance Jobs History", path: "/jobHistory", icon: <HistoryIcon /> },
//         // { text: "All Attendance Jobs History", path: "/jobHistoryAll", icon: <WorkHistoryIcon /> },
//         // { text: "Assign Jobs", path: "/assignJob", icon: <AssignmentIcon /> },
//         // { text: "Over time", path: "/overTimeView", icon: < OT/> },
//         ...(isOTAllowed
//     ? [{ text: "Management Info", path: "/overTimeView", icon: <OverTime /> }]
//     : []),
//     ];
//     // Add "Assign OT" only if jobs are available
//     const menuItems = [
//         ...baseMenuItems,
//         ...(jobValue && jobValue.length > 0 ? [{ text: "Assign OT", path: "/assignOT", icon: <OT /> },
//             { text: "Holiday Assign OT", path: "/holidayOT", icon: <OT /> },
//             { text: "OT Details", path: "/otDetails", icon: <ContentPasteIcon /> },
            
//         ] : []),
//         { text: "Log Out", path: "/logout", icon: <ExitToAppIcon /> },
//     ];
//     const handleNavigation = (path) => {
//         if (path === "/jobHistory" || path === "/jobHistoryAll") {
//             dispatch(fetchJobItems(new Date().toISOString().split("T")[0], employeeId, role));
//         }
//         if (path === "/logout") {
//             localStorage.clear();
//             navigate("/");
//         } else {
//             navigate(path, {
//                 state: { employeeId, department, role },
//             });
//         }
//         toggleDrawer(false);
//     };
//     // Rest of the component remains the same...
//     return (
//         <>
//         <Drawer
//             open={open}
//             onClose={() => toggleDrawer(false)}
//             anchor="left"
//             sx={{
//                 "& .MuiDrawer-paper": {
//                     width: 280,
//                     bgcolor: "transparent",
//                     borderRight: "none",
//                     transition: "transform 0.3s ease",
//                     transform: open ? "translateX(0)" : "translateX(-100%)",
//                     boxShadow: "0 0 15px rgba(0, 0, 0, 0.5)",
//                     borderTopRightRadius: 40,
//                     borderBottomRightRadius: 40,
//                     backdropFilter: "blur(10px)",
//                 },
//             }}
//         >
//             <Box
//                 sx={{
//                     display: "flex",
//                     flexDirection: "column",
//                     height: "100%",
//                     px: 2,
//                     py: 3,
//                     color: "white",
//                 }}
//             >
//                 {/* Header Section */}
//                 <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
//                     <img
//                         src={Logo}
//                         alt="Logo"
//                         style={{
//                             height: 70,
//                             objectFit: "contain",
//                             borderRadius: "5px",
//                         }}
//                     />
//                     <IconButton onClick={() => toggleDrawer(false)} sx={{ color: "white" }}>
//                         <CloseIcon />
//                     </IconButton>
//                 </Box>
//                 <Box
//                     sx={{
//                         display: "flex",
//                         flexDirection: "column",
//                         alignItems: "center",
//                         justifyContent: "center",
//                         mt: 2,
//                         textAlign: "center",
//                         width: "100%",
//                     }}
//                 >
//                     <Box sx={{ position: "relative", mb: 2 }}>
//                         {loadingImage && (
//                             <CircularProgress
//                                 sx={{
//                                     position: "absolute",
//                                     top: "50%",
//                                     left: "50%",
//                                     marginTop: "-65px",
//                                     marginLeft: "-65px",
//                                     color: "#ddd"
//                                 }}
//                                 size={130}
//                             />
//                         )}
//                         <Box
//                             onClick={() => fileInputRef.current?.click()}
//                             sx={{
//                                 position: "relative",
//                                 cursor: uploadingImage ? "not-allowed" : "pointer",
//                                 opacity: uploadingImage ? 0.6 : 1,
//                                 transition: "all 0.3s ease",
//                                 "&:hover": {
//                                     filter: uploadingImage ? "none" : "brightness(0.9)",
//                                     transform: uploadingImage ? "none" : "scale(1.05)",
//                                 },
//                             }}
//                         >
//                             <Avatar
//                                 src={imageError ? DEFAULT_IMAGE : userImg}
//                                 onError={handleImageError}
//                                 onLoad={handleImageLoad}
//                                 sx={{
//                                     width: 130,
//                                     height: 130,
//                                     border: "3px solid #ddd",
//                                     backgroundColor: "#f0f0f0",
//                                 }}
//                             />
//                             {!uploadingImage && (
//                                 <Box
//                                     sx={{
//                                         position: "absolute",
//                                         bottom: 5,
//                                         right: 5,
//                                         backgroundColor: "#1e0153",
//                                         borderRadius: "50%",
//                                         padding: "8px",
//                                         display: "flex",
//                                         alignItems: "center",
//                                         justifyContent: "center",
//                                         color: "white",
//                                         boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
//                                     }}
//                                 >
//                                     <CloudUploadIcon sx={{ fontSize: 20 }} />
//                                 </Box>
//                             )}
//                         </Box>

//                         <input
//                             ref={fileInputRef}
//                             type="file"
//                             accept="image/*"
//                             onChange={handleProfileImageChange}
//                             style={{ display: "none" }}
//                             disabled={uploadingImage}
//                         />
//                     </Box>

//                     {/* Success/Error Messages */}
//                     {uploadSuccess && (
//                         <Alert severity="success" sx={{ mb: 2, width: "100%", fontSize: "0.75rem" }}>
//                             Profile picture updated!
//                         </Alert>
//                     )}
//                     {uploadError && (
//                         <Alert severity="error" sx={{ mb: 2, width: "100%", fontSize: "0.75rem" }}>
//                             {uploadError}
//                         </Alert>
//                     )}

//                     <Typography variant="h6" color="white" sx={{ fontWeight: "bold" }}>
//                         {employeeName || "Loading..."}
//                     </Typography>
//                     <Typography variant="body2" sx={{ color: "#E0A300", mt: 1, fontWeight: "bold" }}>
//                         {department} - {role}
//                     </Typography>
//                     <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.6)", mt: 1 }}>
//                         Click image to upload
//                     </Typography>
//                 </Box>
//                 {/* Menu Items */}
//                 <List sx={{ mt: 4 }}>
//                     {menuItems.map((item) => (
//                         <ListItem
//                             button
//                             key={item.text}
//                             onClick={() => handleNavigation(item.path)}
//                             sx={{
//                                 my: 1,
//                                 backgroundColor: item.path === window.location.pathname ? "#E0A300" : "transparent",
//                                 color: item.path === window.location.pathname ? "white" : "white",
//                                 borderRadius: 2,
//                                 fontWeight: "bold",
//                                 fontSize: 18,
//                                 transition: "all 0.3s ease",
//                                 "&:hover": {
//                                     backgroundColor: "#F2B807",
//                                     transform: "scale(1.05)",
//                                 },
//                             }}
//                         >
//                             <ListItemAvatar>
//                                 <Avatar
//                                     sx={{
//                                         color: item.path === window.location.pathname ? "white" : "white",
//                                         backgroundColor: "transparent",
//                                     }}
//                                 >
//                                     {item.icon}
//                                 </Avatar>
//                             </ListItemAvatar>
//                             <ListItemText primary={item.text} />
//                         </ListItem>
//                     ))}
//                 </List>
//             </Box>
//         </Drawer>

//         {/* Image Preview Dialog */}
//         <Dialog
//             open={openPreviewDialog}
//             onClose={handleClosePreview}
//             maxWidth="sm"
//             fullWidth
//             sx={{
//                 "& .MuiDialog-paper": {
//                     borderRadius: 2,
//                 }
//             }}
//         >
//             <DialogTitle sx={{ fontWeight: 700, color: "#1e0153" }}>
//                 Preview Profile Picture
//             </DialogTitle>
//             <DialogContent sx={{ py: 3, textAlign: "center" }}>
//                 {previewImage && (
//                     <Box>
//                         <Avatar
//                             src={previewImage}
//                             sx={{
//                                 width: 250,
//                                 height: 250,
//                                 mx: "auto",
//                                 mb: 2,
//                                 border: "3px solid #1e0153",
//                             }}
//                         />
//                         <Typography variant="body2" sx={{ color: "#666", mt: 2 }}>
//                             Does this look good? Click "Save" to update your profile picture.
//                         </Typography>
//                     </Box>
//                 )}
//             </DialogContent>
//             <DialogActions sx={{ p: 2, gap: 1 }}>
//                 <Button
//                     onClick={handleClosePreview}
//                     variant="outlined"
//                     sx={{
//                         color: "#999",
//                         borderColor: "#ccc",
//                         "&:hover": { borderColor: "#999" }
//                     }}
//                     disabled={uploadingImage}
//                 >
//                     Cancel
//                 </Button>
//                 <Button
//                     onClick={handleSaveImage}
//                     variant="contained"
//                     sx={{
//                         bgcolor: "#1e0153",
//                         "&:hover": { bgcolor: "#333" }
//                     }}
//                     disabled={uploadingImage}
//                 >
//                     {uploadingImage ? (
//                         <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                             <CircularProgress size={20} sx={{ color: "white" }} />
//                             Saving...
//                         </Box>
//                     ) : (
//                         "Save"
//                     )}
//                 </Button>
//             </DialogActions>
//         </Dialog>
//         </>
//     );
// };
// export default Sidebar;

















// import React, { useEffect, useState } from "react";
// import {
//     Avatar,
//     Box,
//     Drawer,
//     IconButton,
//     List,
//     ListItem,
//     ListItemAvatar,
//     ListItemText,
//     Typography,
// } from "@mui/material";
// import {
//     Assignment as AssignmentIcon,
//     CalendarToday as CalendarTodayIcon,
//     Close as CloseIcon,
//     ExitToApp as ExitToAppIcon,
//     History as HistoryIcon,
//     Home as HomeIcon,
//     WorkHistory as WorkHistoryIcon,
//     MoreTime as OT,
//     ContentPaste as ContentPasteIcon,
//     Addchart as OverTime,
// } from "@mui/icons-material";
// import { useDispatch, useSelector } from "react-redux";
// import { useNavigate } from "react-router-dom";
// import { fetchJobItems } from "../../actions/jobAction";
// import { fetchUserData } from "../../service/authService";
// import { fetchJobs } from "../../actions/AssignAction";
// import Logo from "../../assets/logo6.png";
// const Sidebar = ({ open, toggleDrawer }) => {
//     const dispatch = useDispatch();
//     const navigate = useNavigate();
//     const [phoneNumber, setPhoneNumber] = useState(localStorage.getItem("phoneNumber") || "");
//     const [employeeId, setEmployeeId] = useState(localStorage.getItem("employeeId") || "");
//     const [employeeName, setEmployeeName] = useState(localStorage.getItem("employeeName") || "");
//     const [department, setDepartment] = useState(localStorage.getItem("department") || "");
//     const [role, setRole] = useState(localStorage.getItem("role") || "");
//     const [userImg, setUserImg] = useState("");
//     // Get jobs data from Redux store
//     const { jobValue } = useSelector((state) => state.assignJob || {});
//     // Update userImg dynamically when employeeId changes
//     useEffect(() => {
//         if (employeeId) {
//             setUserImg(`https://esystems.cdl.lk/backend/AttendMe2/Home/GetDTSUserImg?serviceNo=${employeeId}`);
//         }
//         const date = new Date().toISOString().split("T")[0];
//         dispatch(fetchJobs(date, employeeId));
//     }, [employeeId]);
    


//     useEffect(() => {
//         if (phoneNumber && !employeeId) {
//             const fetchUserDataAsync = async () => {
//                 try {
//                     const response = await fetchUserData(phoneNumber);
                    
//                     // Check both responceType and responceMessage
//                     if (response?.responceType === true && response?.responceMessage) {
//                         const [empId, empName, dept, role] = response.responceMessage.split("/");
                        
//                         setEmployeeId(empId);
//                         setEmployeeName(empName);
//                         setDepartment(dept);
//                         setRole(role);
                        
//                         localStorage.setItem("employeeId", empId);
//                         localStorage.setItem("employeeName", empName);
//                         localStorage.setItem("department", dept);
//                         localStorage.setItem("role", role);
//                         localStorage.setItem("correctPhoneFormat", response.correctPhoneFormat);
//                     }
//                 } catch (error) {
//                     console.error("Failed to fetch user data:", error);
//                 }
//             };
//             fetchUserDataAsync();
//         }
//     }, [phoneNumber, employeeId]);

//    console.log("Employee ID value:", employeeId);

//     // Define base menu items 
//     const allowedOTUsers = ["3000158","3000159","3000694","1004507","3001230","3000821","0001809"];
//     const isOTAllowed = allowedOTUsers.includes(String(employeeId));

//     const baseMenuItems = [
//         { text: "Home", path: "/home", icon: <HomeIcon /> },
//         { text: "Today Attendance", path: "/todayAttendance", icon: <CalendarTodayIcon /> },
//         // { text: "Attendance Jobs History", path: "/jobHistory", icon: <HistoryIcon /> },
//         // { text: "All Attendance Jobs History", path: "/jobHistoryAll", icon: <WorkHistoryIcon /> },
//         // { text: "Assign Jobs", path: "/assignJob", icon: <AssignmentIcon /> },
//         // { text: "Over time", path: "/overTimeView", icon: < OT/> },
//         ...(isOTAllowed
//     ? [{ text: "Management Info", path: "/overTimeView", icon: <OverTime /> }]
//     : []),
//     ];
//     // Add "Assign OT" only if jobs are available
//     const menuItems = [
//         ...baseMenuItems,
//         ...(jobValue && jobValue.length > 0 ? [{ text: "Assign OT", path: "/assignOT", icon: <OT /> },
//             { text: "Holiday Assign OT", path: "/holidayOT", icon: <OT /> },
//             { text: "OT Details", path: "/otDetails", icon: <ContentPasteIcon /> },
            
//         ] : []),
//         { text: "Log Out", path: "/logout", icon: <ExitToAppIcon /> },
//     ];
//     const handleNavigation = (path) => {
//         if (path === "/jobHistory" || path === "/jobHistoryAll") {
//             dispatch(fetchJobItems(new Date().toISOString().split("T")[0], employeeId, role));
//         }
//         if (path === "/logout") {
//             localStorage.clear();
//             navigate("/");
//         } else {
//             navigate(path, {
//                 state: { employeeId, department, role },
//             });
//         }
//         toggleDrawer(false);
//     };
//     // Rest of the component remains the same...
//     return (
//         <Drawer
//             open={open}
//             onClose={() => toggleDrawer(false)}
//             anchor="left"
//             sx={{
//                 "& .MuiDrawer-paper": {
//                     width: 280,
//                     bgcolor: "transparent",
//                     borderRight: "none",
//                     transition: "transform 0.3s ease",
//                     transform: open ? "translateX(0)" : "translateX(-100%)",
//                     boxShadow: "0 0 15px rgba(0, 0, 0, 0.5)",
//                     borderTopRightRadius: 40,
//                     borderBottomRightRadius: 40,
//                     backdropFilter: "blur(10px)",
//                 },
//             }}
//         >
//             <Box
//                 sx={{
//                     display: "flex",
//                     flexDirection: "column",
//                     height: "100%",
//                     px: 2,
//                     py: 3,
//                     color: "white",
//                 }}
//             >
//                 {/* Header Section */}
//                 <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
//                     <img
//                         src={Logo}
//                         alt="Logo"
//                         style={{
//                             height: 70,
//                             objectFit: "contain",
//                             borderRadius: "5px",
//                         }}
//                     />
//                     <IconButton onClick={() => toggleDrawer(false)} sx={{ color: "white" }}>
//                         <CloseIcon />
//                     </IconButton>
//                 </Box>
//                 <Box
//                     sx={{
//                         display: "flex",
//                         flexDirection: "column",
//                         alignItems: "center",
//                         justifyContent: "center",
//                         mt: 2,
//                         textAlign: "center",
//                         width: "100%",
//                     }}
//                 >
//                     <Avatar
//                             src={  "../../assets/logo6.png"}
//                             onError={() => setUserImg("/path/to/your/local-image.png")}
//                             sx={{
//                                 width: 130,
//                                 height: 130,
//                                 mb: 2,
//                                 border: "3px solid #ddd",
//                             }}
// />


//                     <Typography variant="h6" color="white" sx={{ fontWeight: "bold" }}>
//                         {employeeName || "Loading..."}
//                     </Typography>
//                     <Typography variant="body2" sx={{ color: "#E0A300", mt: 1, fontWeight: "bold" }}>
//                         {department} - {role}
//                     </Typography>
//                 </Box>
//                 {/* Menu Items */}
//                 <List sx={{ mt: 4 }}>
//                     {menuItems.map((item) => (
//                         <ListItem
//                             button
//                             key={item.text}
//                             onClick={() => handleNavigation(item.path)}
//                             sx={{
//                                 my: 1,
//                                 backgroundColor: item.path === window.location.pathname ? "#E0A300" : "transparent",
//                                 color: item.path === window.location.pathname ? "white" : "white",
//                                 borderRadius: 2,
//                                 fontWeight: "bold",
//                                 fontSize: 18,
//                                 transition: "all 0.3s ease",
//                                 "&:hover": {
//                                     backgroundColor: "#F2B807",
//                                     transform: "scale(1.05)",
//                                 },
//                             }}
//                         >
//                             <ListItemAvatar>
//                                 <Avatar
//                                     sx={{
//                                         color: item.path === window.location.pathname ? "white" : "white",
//                                         backgroundColor: "transparent",
//                                     }}
//                                 >
//                                     {item.icon}
//                                 </Avatar>
//                             </ListItemAvatar>
//                             <ListItemText primary={item.text} />
//                         </ListItem>
//                     ))}
//                 </List>
//             </Box>
//         </Drawer>
//     );
// };
// export default Sidebar;